// src/scripts/kiroku-embed.ts
/*
 * KirokuForms Dynamic Embed Script
 * Supports two modes:
 *   - 'html': Fetches HTML, injects it, initializes kiroku-form.js handlers
 *   - 'react': Fetches JSON, dynamically imports React, renders LiveForm component
 *
 * Usage:
 *   <div id="kiroku-form"></div>
 *   <script src="https://www.kirokuforms.com/embed.js"
 *           data-kiroku-form-id="FORM_ID"
 *           data-kiroku-target="#kiroku-form"
 *           data-kiroku-mode="html"
 *           async></script>
 *
 * Embedding on a NEW domain and want the Turnstile anti-bot check active
 * there? Two separate things both have to be true, not just one:
 *   1. The form's "Allowed Origins" setting must list that domain.
 *   2. That domain must be registered on the ONE shared Cloudflare Turnstile
 *      widget every KirokuForms embed uses (src/lib/utils/turnstileWidget.ts)
 *      -- Cloudflare refuses to run a challenge on a hostname it doesn't
 *      know about, silently, in the visitor's browser.
 * Saving the form with Turnstile switched on triggers an automatic sync that
 * registers the domain (docs/turnstile-coverage-gate_plan.md) -- but verify
 * it actually landed via the settings panel's own coverage warning ("This
 * check is not active yet") or GET /api/forms/[id]/turnstile-status, rather
 * than assuming the toggle alone did it. Registrations do not happen by
 * embedding the script; they happen by saving the form's own settings.
 */

// Preact is bundled into this script at build time (scripts/build-embed.mjs via
// esbuild). The React-mode embed renders with Preact's compat layer instead of
// fetching ~44KB of React + ReactDOM from unpkg at runtime.
import { createElement, render } from "preact/compat";
import {
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useEffect,
} from "preact/hooks";
import { buildClientMeta } from "../lib/utils/clientMetadata";
import { TURNSTILE_APPEARANCE } from "../config/turnstile";
import { autocompleteToken } from "../lib/utils/autocompleteToken";
import { attributionHref } from "../lib/utils/attributionLink";
import { safeNavigationUrl } from "../lib/utils/safeUrl";
import { RICH_TEXT_STYLES } from "../lib/richText/styles";
import {
  resolveConsentPrivacyLink,
  CONSENT_PRIVACY_LINK_TEXT,
} from "../lib/forms/consentFields";
import {
  needsDefaultHoneypotField,
  buildDefaultHoneypotField,
  FILL_TIME_TOKEN_FIELD,
} from "../lib/utils/formHoneypot";
// The one implementation of every logic operator (plan 1.3). This file carried
// a third inlined copy of the switch under a comment reading "Ported from
// src/lib/utils/logicEngine.ts", and a port is a fork: its `equals` matched an
// array with `.some()` rather than a set comparison and its `contains` never
// looked inside one, so a multi-select answered rules here that it failed on
// the hosted page. That divergence is bug-037.
import {
  compareValue,
  hasLogicRules,
  evaluateFieldVisibility,
} from "../lib/utils/logicEngine";
// The one sentinel and its predicates, shared with LiveForm and
// kiroku-form.ts (plan form-design-gaps, 3.2), so "Other" means the same
// literal value and the same "is this the active choice" rule everywhere.
import {
  OTHER_OPTION_VALUE,
  isOtherValue,
  stripOtherSentinel,
} from "../lib/utils/otherOption";
// Plan 5.1 of docs/guided-shopping_plan.md: a guided-shopping funnel's
// wizard-then-result experience, shared with LiveForm.tsx's
// GuidedFunnelRenderer via the one pure content builder -- "same answers,
// same screen, both renderers" is `resultScreen.ts`'s whole point.
import { compileFunnel } from "../lib/guided/compile";
import { buildResultScreen } from "../lib/guided/resultScreen";
import { fixedPathLength } from "../lib/guided/lints";
import { buildUpFrontScreen } from "../lib/guided/upFrontScreen";
import { ownerContactLink } from "../lib/guided/ownerContact";
import { concernActionLink } from "../lib/guided/concernAction";
import { withConcernOpened } from "../lib/guided/concernsOpened";
import type { FunnelConfig } from "../lib/guided/config";
import { ratingScale, ratingPoints } from "../config/ratingScale";
// The one outcome-quiz evaluator, shared with LiveForm, the html-mode embed and
// the server recompute (plan 6.4; guard tests/guard/quiz-renderers-share-one-
// evaluator.test.ts). This file is bundled by esbuild, so importing it costs
// only the tally itself.
import {
  OUTCOME_DISMISS_CLASS,
  OUTCOME_DISMISS_LABEL,
  outcomeCtaHref,
  tallyOutcomes,
} from "../lib/quiz/outcomes";
import type { QuizFormLike } from "../lib/quiz/outcomes";
import type { QuizResult } from "../lib/types";
import {
  createPopupTrigger,
  mobilePopupSettingsToTrigger,
  popupSettingsToTrigger,
  VM_INTENT_EVENT,
} from "./popupTriggers";
// Where in the stored settings a consent config lives, promoted out of
// `settings.popup` by task 0.1 with the nested location still read. One
// implementation, so this renderer and LiveForm cannot drift apart.
import {
  trackingConsentFromSettings,
  resolveTrackingConsent,
} from "./trackingConsent";
// What counts as a visitor starting to answer a form, shared with LiveForm.tsx
// and kiroku-form.ts so all three renderers agree on one form_started count
// (task 1.1, docs/popup-triggers-and-funnel_plan.md).
import { isAnsweringControl } from "../lib/analytics/interaction";
import {
  openModal,
  closeModal,
  DECLINE_BUTTON_STYLE,
  DECLINE_ROW_STYLE,
  DEFAULT_DECLINE_TEXT,
} from "./popupModal";
// docs/visitor-check-choice_plan.md Phase 2, task 2.2: the self-hosted
// built-in visitor check, alongside Turnstile. Relative imports, like every
// other import in this file (scripts/build-embed.mjs's esbuild options carry
// no `@` alias).
import { loadAltchaWidget } from "./altcha-loader";
import { effectiveVisitorCheck } from "../lib/utils/visitorCheck";
import type {
  Inline,
  RichTextBlock,
  RichTextDocument,
  TextRun,
} from "../lib/richText/types";

/**
 * How long a popup keeps its success message on screen after a submit before
 * it closes. Long enough to read a one-line thank-you; the visitor can close
 * it sooner with the ×, Escape or a click outside. Lives here, not in
 * popupModal.ts, because the speed guard builds the embed against a stub of
 * that module.
 */
const POPUP_SUCCESS_VISIBLE_MS = 4000;

/*
 * The React-mode embed's own node builder for a rich text document
 * (docs/form-text-formatting_plan.md task 1.3), matching
 * `src/components/forms/RichText.tsx` element for element. A separate
 * implementation rather than a shared import: this file builds its tree with
 * `createElement` from `preact/compat` (imported above as `createElement`),
 * and the HTML-mode embed (`?mode=html`) never reaches this code at all --
 * that markup comes pre-rendered from `formEmbedUtils.ts`, which has its own
 * string-building version of the same logic.
 */
function richTextRunNode(run: TextRun, key: string): any {
  let node: any = run.text;
  if (run.italic) node = createElement("em", { key: `${key}-i` }, node);
  if (run.bold) node = createElement("strong", { key: `${key}-b` }, node);
  if (run.href) {
    const href = safeNavigationUrl(run.href);
    if (href) {
      node = createElement(
        "a",
        {
          key: `${key}-a`,
          href,
          target: "_blank",
          rel: "noopener noreferrer",
        },
        node,
      );
    }
  }
  return node;
}

function richTextInlineNodes(content: Inline[], keyPrefix: string): any[] {
  return content.map((item, index) => {
    const key = `${keyPrefix}-${index}`;
    return "lineBreak" in item
      ? createElement("br", { key })
      : richTextRunNode(item, key);
  });
}

function richTextListItemNode(item: TextRun[], key: string): any {
  return createElement(
    "li",
    { key },
    item.map((run, index) => richTextRunNode(run, `${key}-${index}`)),
  );
}

function richTextBlockNode(block: RichTextBlock, key: string): any {
  switch (block.type) {
    case "paragraph":
      return createElement(
        "p",
        { key, style: RICH_TEXT_STYLES.paragraph },
        richTextInlineNodes(block.content, key),
      );
    case "heading":
      return createElement(
        block.level === 1 ? "h3" : "h4",
        {
          key,
          style:
            block.level === 1
              ? RICH_TEXT_STYLES.heading
              : RICH_TEXT_STYLES.subheading,
        },
        richTextInlineNodes(block.content, key),
      );
    case "bulletList":
      return createElement(
        "ul",
        { key, style: RICH_TEXT_STYLES.bulletList },
        block.items.map((item, index) =>
          richTextListItemNode(item, `${key}-${index}`),
        ),
      );
    case "numberedList":
      return createElement(
        "ol",
        { key, style: RICH_TEXT_STYLES.numberedList },
        block.items.map((item, index) =>
          richTextListItemNode(item, `${key}-${index}`),
        ),
      );
    default:
      return null;
  }
}

/** Every block, in order. For the description and a Text block. */
function richTextBlockNodes(doc: RichTextDocument, keyPrefix: string): any[] {
  return doc.blocks.map((block, index) =>
    richTextBlockNode(block, `${keyPrefix}-${index}`),
  );
}

/**
 * Only the first paragraph's runs, with no wrapping block element -- for
 * consent wording, which sits inside a `<label>`.
 */
function richTextInlineOnlyNodes(
  doc: RichTextDocument,
  keyPrefix: string,
): any[] {
  const first = doc.blocks[0];
  if (!first || first.type !== "paragraph") return [];
  return richTextInlineNodes(first.content, keyPrefix);
}

interface EmbedConfig {
  formId: string;
  target?: string; // CSS selector for container
  mode: "html" | "react";
  css: "inline" | "external";
  apiUrl: string; // Base API URL
  debug: boolean;
  /** data-kiroku-trigger: per-placement override of settings.popup.trigger. */
  triggerOverride?: string;
  /**
   * data-kiroku-popup="false": render inline even when the form is set to open
   * as a popup. Without it, an HTML-mode placement of a popup form hands over
   * to the popup path (embedHtmlMode). The WordPress plugin sets it for
   * `popup="false"`.
   */
  popupOptOut?: boolean;
}

/** Options for the programmatic window.KirokuForms.open(formId, opts) API. */
interface OpenOptions {
  apiUrl?: string;
  css?: "inline" | "external";
  debug?: boolean;
  /** Field-name → value seed; wins over kf_ URL prefill and field defaults. */
  values?: Record<string, string>;
}

// This file is now an ES module (it imports Preact), so the Window augmentation
// must be wrapped in `declare global` to stay a global ambient declaration.
// The KirokuForms shape below is declaration-merged with the identical one in
// kiroku-form.ts — keep the two in sync or type-check fails.
declare global {
  interface Window {
    KirokuEmbed?: {
      version: string;
      init: (config: Partial<EmbedConfig>) => Promise<void>;
    };
    KirokuForms?: {
      version: string;
      init?: (form: HTMLFormElement) => void;
      initAll?: () => void;
      open?: (
        formId: string,
        opts?: {
          apiUrl?: string;
          css?: "inline" | "external";
          debug?: boolean;
          values?: Record<string, string>;
        },
      ) => Promise<void>;
      debug?: boolean;
    };
    // Set once when the delegated open listeners are bound, so multiple copies
    // of embed.js on one page don't double-register document handlers.
    __kirokuOpenApiBound?: boolean;
  }
}

(function () {
  "use strict";

  // ============================================================================
  // LOGIC ENGINE: the value lookup only. Every operator lives in compareValue
  // (src/lib/utils/logicEngine.ts), imported above.
  // ============================================================================

  function evaluateFieldVisibility(
    field: any,
    formValues: any,
    allFields: any[],
  ): boolean {
    if (!field.logic) return true; // No logic = always visible

    try {
      // Parse logic if it's a string (from attribute), otherwise use object
      const logic =
        typeof field.logic === "string" ? JSON.parse(field.logic) : field.logic;

      // The shared predicate, not a local retelling of it: `!logic.conditions`
      // let a non-array `conditions` through and `.map` threw on it below.
      if (!hasLogicRules(logic)) return true;

      const results = logic.conditions.map((cond: any) => {
        // Find the trigger field to get its name
        const triggerField = allFields.find((f: any) => f.id === cond.fieldId);
        if (!triggerField) return false;

        const triggerValue = formValues[triggerField.name];

        return compareValue(cond.operator, triggerValue, cond.value);
      });

      const conditionsMet =
        logic.conditionType === "any"
          ? results.some((r: boolean) => r)
          : results.every((r: boolean) => r);

      return logic.action === "show" ? conditionsMet : !conditionsMet;
    } catch (e) {
      console.warn("Logic evaluation error", e);
      return true; // Fallback to visible
    }
  }

  const VERSION = "1.0.0";
  const DEFAULT_API_URL = "https://www.kirokuforms.com";

  function parseOptions(
    options: any,
  ): { label: string; value: string; richLabel?: RichTextDocument | null }[] {
    if (!options) return [];
    if (Array.isArray(options)) return options;
    if (typeof options === "string") {
      try {
        return JSON.parse(options);
      } catch (e) {
        return [];
      }
    }
    return [];
  }

  // === OUTCOME QUIZ (plan 6.4) ===

  /**
   * The form as the shared evaluator reads it.
   *
   * `GET /api/forms/<id>/embed?mode=json` already returns `settings` and
   * `fields` verbatim, so `settings.outcomes` and each option's `outcomeIds`
   * arrive here with no extra plumbing: this mode needed no equivalent of the
   * `data-kiroku-quiz` attribute 6.1 had to add for the html-mode embed, which
   * only ever sees generated markup.
   *
   * Options are normalised through `parseOptions` for the same reason the
   * renderer below calls it: a legacy row can hold its options as a JSON string
   * inside the Json column, and the evaluator reads an array. Left unparsed
   * that field would silently contribute no votes, which is a wrong result
   * rather than a visible failure.
   */
  function quizDefinitionFrom(formData: any): QuizFormLike {
    const fields = Array.isArray(formData?.fields) ? formData.fields : [];
    return {
      settings: formData?.settings ?? null,
      fields: fields.map((field: any) => ({
        name: field?.name,
        label: field?.label ?? null,
        options: parseOptions(field?.options),
      })),
    };
  }

  /**
   * The result screen, in place of the form (plan 6.4).
   *
   * The third implementation of one screen, and deliberately so: decision 0.2
   * chose to build it in every renderer rather than share a component none of
   * them could import. `renderQuizResult` in src/components/forms/LiveForm.tsx
   * and `createOutcomeElement` in src/scripts/kiroku-form.ts are the other two.
   * All three agree on the *answer* because they call `tallyOutcomes`, which is
   * guard 5.2; they agree on what is on screen because of this comment and the
   * tests in tests/embed-react-shows-an-outcome.test.ts.
   *
   * Every string here is authored in our dashboard and rendered on somebody
   * else's page, so all three parts are text children and nothing goes through
   * `dangerouslySetInnerHTML`. The CTA href goes through the shared
   * `outcomeCtaHref`, which refuses `javascript:` and upgrades a bare host.
   *
   * `role="status"` here and not in the html-mode embed: that one appends into
   * `.kiroku-form-messages`, which is already a live region, and nesting two
   * gets the result announced twice. This one replaces the form, so it has no
   * live-region ancestor and needs its own.
   *
   * The class names are the ones src/lib/utils/formStyleUtils.ts already ships
   * in /css/kiroku-form.css (added by 6.2), which `loadCSS` fetches before this
   * component mounts. This embed has no Tailwind, so without them the result
   * would render as browser defaults on a customer's page.
   */
  /**
   * The form's `--kiroku-*` declarations as a Preact `style` object, so they
   * land as inline custom properties on the form and the result screen.
   *
   * Inline because /css/kiroku-form.css defines the default set *on*
   * `.kiroku-embed-form` (formStyleUtils.ts), and a class rule outranks
   * anything inherited from the popup card: until 2026-09-17 the card was
   * tinted to the theme (plan D) and the form inside it stayed indigo, which
   * is the "defaults to the KirokuForms branding" half of that day's report.
   * The hosted page already does this through LiveForm's `themeVars` prop.
   * Undefined when the JSON carries no `themeVars` (an older cached
   * response), which leaves the stylesheet's defaults exactly as before.
   */
  function themeStyleFrom(vars: unknown): Record<string, string> | undefined {
    if (typeof vars !== "string" || !vars.trim()) return undefined;
    const style: Record<string, string> = {};
    for (const declaration of vars.split(";")) {
      const separator = declaration.indexOf(":");
      if (separator === -1) continue;
      const name = declaration.slice(0, separator).trim();
      const value = declaration.slice(separator + 1).trim();
      if (name.startsWith("--") && value) style[name] = value;
    }
    return Object.keys(style).length > 0 ? style : undefined;
  }

  function createOutcomeNode(
    result: QuizResult,
    fallbackMessage: string,
    onDismiss: () => void,
  ): any {
    switch (result.type) {
      case "outcome": {
        const title = (result.title || "").trim();
        const body = (result.body || "").trim();

        // A half-authored outcome (an id and its tags, no words) has nothing a
        // visitor can read, and a blank panel after submitting reads as a
        // broken form. Same fallback as the other two renderers.
        if (!title && !body) {
          return createElement(
            "div",
            { className: "kiroku-form-messages", "aria-live": "polite" },
            createElement(
              "div",
              { className: "kiroku-form-message kiroku-form-success" },
              fallbackMessage,
            ),
          );
        }

        const href = outcomeCtaHref(result.cta);
        return createElement(
          "div",
          {
            className: "kiroku-form-message kiroku-form-outcome",
            "data-outcome-id": result.id,
            role: "status",
            "aria-live": "polite",
          },
          [
            title &&
              createElement(
                "h2",
                { key: "title", className: "kiroku-form-outcome-title" },
                title,
              ),
            body &&
              createElement(
                "p",
                { key: "body", className: "kiroku-form-outcome-body" },
                body,
              ),
            href &&
              result.cta &&
              createElement(
                "a",
                {
                  key: "cta",
                  className: "kiroku-form-outcome-cta",
                  href,
                  rel: "noopener noreferrer",
                },
                result.cta.label,
              ),
            // The "no" to the CTA's yes, only when there is a CTA to refuse.
            // What it does is the caller's: in a popup it closes the modal,
            // inline it brings back the plain thank-you (see dismissOutcome
            // in the component below).
            href &&
              result.cta &&
              createElement(
                "button",
                {
                  key: "dismiss",
                  type: "button",
                  className: OUTCOME_DISMISS_CLASS,
                  onClick: onDismiss,
                },
                OUTCOME_DISMISS_LABEL,
              ),
          ],
        );
      }
      default: {
        // Plan 1.2: a second QuizResult variant (score bands) fails to compile
        // here rather than rendering nothing.
        const unhandled: never = result.type;
        throw new Error(`Unhandled quiz result: ${String(unhandled)}`);
      }
    }
  }

  /**
   * What a visitor is told when the server refused and gave us nothing
   * readable, and when the request never reached it at all.
   *
   * Neither a JSON parser's complaint ("Unexpected token '<'") nor fetch's
   * own ("Failed to fetch") is a sentence written for somebody filling in a
   * form, so neither is ever rendered. The real error goes to the console.
   */
  const GENERIC_SUBMIT_ERROR = "Submission failed. Please check your inputs.";
  const UNREACHABLE_SERVER_ERROR =
    "Could not reach the server. Please check your connection and try again.";

  /**
   * What a visitor is told when the anti-bot challenge has not produced a
   * token, in the three ways that happens.
   *
   * "Security check" rather than Turnstile, Cloudflare or CAPTCHA: the person
   * reading this is filling in a form on somebody else's website and has no
   * reason to know whose widget it is. Each sentence says what to do, because
   * a disabled button with no explanation is the failure this exists to
   * prevent.
   */
  const TURNSTILE_PENDING_ERROR =
    "Please complete the security check before submitting.";
  const TURNSTILE_FAILED_ERROR =
    "The security check could not be completed. Please reload the page and try again.";
  const TURNSTILE_EXPIRED_ERROR =
    "The security check expired. Please complete it again.";
  // The same expiry, when a replacement challenge has already been asked for.
  // It reads as a wait rather than as an instruction, because the usual
  // outcome is that nothing is required of the visitor at all. It still says
  // what to do in the other outcome: a reset re-runs the challenge, and an
  // interactive one puts a checkbox back on screen for them to tick.
  const TURNSTILE_REFRESHING_MESSAGE =
    "The security check expired and is renewing. Complete it again if it asks you to.";
  // The built-in check (task 2.2) needs no click, so unlike the three above
  // there is nothing for the visitor to "complete" -- only a moment to wait.
  const ALTCHA_WAIT_MESSAGE =
    "Please wait a moment while we finish a quick security check.";

  /**
   * Replace the form-level message area with one error sentence.
   *
   * textContent, never innerHTML: these strings reach here from the server and
   * from this file, and a message is not a place to run markup.
   */
  function showFormError(form: HTMLElement, message: string): void {
    const msgContainer = form.querySelector(".kiroku-form-messages");
    if (!msgContainer) return;
    msgContainer.innerHTML = "";
    const el = document.createElement("div");
    el.className = "kiroku-form-message kiroku-form-error";
    el.textContent = message;
    msgContainer.appendChild(el);
  }

  /**
   * Read a response body as the API envelope, or null when it is not one.
   *
   * `handleApiRequest` always answers with `{success, message, error}`, but the
   * embed runs on somebody else's page and the response it gets is not always
   * ours: a platform 502, a proxy, a captive portal or an offline cache
   * answers with HTML, and `response.json()` throws on it. A body of literal
   * `null` parses cleanly and then throws on the first property read. Both
   * become null here, and the caller falls back to the generic message.
   */
  async function readApiEnvelope(response: Response): Promise<any | null> {
    try {
      const parsed = await response.json();
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  /**
   * The most readable sentence an error envelope carries.
   *
   * `createApiResponse` writes two: `error.message` for the developer and the
   * top-level `message` for the visitor. They are usually identical; where
   * they differ the top level is the fuller one ("Too many submissions." vs
   * "Too many submissions. Please try again later."), so it is the fallback
   * rather than nothing.
   */
  function errorMessageFrom(result: any): string {
    const candidates = [result?.error?.message, result?.message];
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim() !== "") {
        return candidate;
      }
    }
    return GENERIC_SUBMIT_ERROR;
  }

  /**
   * Rejections the submit endpoint answers *before* it verifies the challenge.
   *
   * The order in `src/pages/api/forms/[id].ts` is rate limit (:129), Turnstile
   * (:173), then payload validation (:196), so a token is spent by everything
   * from the second step down and untouched by the first. Only the rate limit
   * is worth naming: it is the one rejection above the verification that a
   * visitor clears by waiting and trying again, so its token has a retry left
   * to serve. The others up there (a wrong origin, an inactive form, an
   * unparseable body) are configuration failures no retry fixes, and giving up
   * a token that nothing will use again costs nothing.
   *
   * `handleApiRequest` and the endpoint both write this code as a literal, so
   * `tests/guard/turnstile-reset-matches-server-order.test.ts` pins the string
   * and the ordering it depends on.
   */
  const PRE_VERIFICATION_ERROR_CODES = new Set(["RATE_LIMIT_EXCEEDED"]);

  /** Did the server refuse this before it could have consumed the token? */
  function isPreVerificationRejection(result: any): boolean {
    const code = result?.error?.code;
    return typeof code === "string" && PRE_VERIFICATION_ERROR_CODES.has(code);
  }

  /**
   * Split the server's per-field validation map into messages that have a
   * field group to live in, and messages that do not.
   *
   * `src/pages/api/forms/[id].ts:223` sends `error.details` keyed by field
   * name. Two kinds of key never match a rendered group: `_form`, which
   * validation.ts sets for a honeypot rejection, and a field on a step the
   * user is not looking at. Those go to the form-level message area, because
   * attaching a message to a group that is off screen is the same as throwing
   * it away.
   *
   * Non-string values are dropped rather than coerced: "[object Object]"
   * beside a field is worse than the generic message on its own.
   */
  function splitValidationDetails(
    details: any,
    renderedFieldNames: Set<string>,
  ): { perField: Record<string, string>; formLevel: string[] } {
    const perField: Record<string, string> = {};
    const formLevel: string[] = [];
    if (!details || typeof details !== "object" || Array.isArray(details)) {
      return { perField, formLevel };
    }
    for (const [key, value] of Object.entries(details)) {
      if (typeof value !== "string" || value.trim() === "") continue;
      if (renderedFieldNames.has(key)) {
        perField[key] = value;
      } else {
        formLevel.push(value);
      }
    }
    return { perField, formLevel };
  }

  /**
   * Parse configuration from a specific script tag's data attributes.
   * Element-scoped so each embed on a page initializes independently.
   */
  function parseConfigFromElement(
    scriptEl: HTMLScriptElement,
  ): Partial<EmbedConfig> | null {
    const formId = scriptEl.getAttribute("data-kiroku-form-id");
    if (!formId) {
      console.error("KirokuEmbed: data-kiroku-form-id attribute is required");
      return null;
    }

    return {
      formId,
      target: scriptEl.getAttribute("data-kiroku-target") || undefined,
      mode: (scriptEl.getAttribute("data-kiroku-mode") || "html") as
        | "html"
        | "react",
      css: (scriptEl.getAttribute("data-kiroku-css") || "external") as
        | "inline"
        | "external",
      apiUrl: scriptEl.getAttribute("data-kiroku-api-url") || DEFAULT_API_URL,
      debug: scriptEl.getAttribute("data-kiroku-debug") === "true",
      triggerOverride:
        scriptEl.getAttribute("data-kiroku-trigger") || undefined,
      popupOptOut: scriptEl.getAttribute("data-kiroku-popup") === "false",
    };
  }

  /**
   * Find or create target container
   */
  function resolveTarget(
    targetSelector?: string,
    scriptElement?: HTMLScriptElement,
  ): HTMLElement | null {
    // Priority 1: User-specified target selector
    if (targetSelector) {
      const element = document.querySelector(targetSelector);
      if (element instanceof HTMLElement) {
        return element;
      }
      console.warn(
        `KirokuEmbed: Target selector "${targetSelector}" not found`,
      );
    }

    // Priority 2: Inject after script tag
    if (scriptElement) {
      const container = document.createElement("div");
      container.className = "kiroku-embed-container";
      scriptElement.parentNode?.insertBefore(
        container,
        scriptElement.nextSibling,
      );
      return container;
    }

    // Priority 3: Append to body (last resort)
    console.warn("KirokuEmbed: No target found, appending to document.body");
    const container = document.createElement("div");
    container.className = "kiroku-embed-container";
    document.body.appendChild(container);
    return container;
  }

  /**
   * Load external CSS if not already loaded
   */
  async function loadCSS(apiUrl: string): Promise<void> {
    const cssUrl = `${apiUrl}/css/kiroku-form.css?v=1.0.0`;

    // Check if already loaded
    if (document.querySelector(`link[href="${cssUrl}"]`)) {
      return;
    }

    return new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cssUrl;
      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load CSS: ${cssUrl}`));
      document.head.appendChild(link);
    });
  }

  /**
   * Load external script
   */
  async function loadScript(url: string): Promise<void> {
    // Check if already loaded
    if (document.querySelector(`script[src="${url}"]`)) {
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${url}`));
      document.head.appendChild(script);
    });
  }

  /**
   * The Cloudflare challenge script, requested in explicit-render mode.
   *
   * `render=explicit` on purpose. Left implicit, api.js scans the document for
   * `.cf-turnstile` elements once, as it loads, and never again. Every form
   * this embed draws arrives after that scan, so the implicit path would leave
   * the container empty on exactly the pages this exists for. HTML mode can use
   * the implicit form (`generateAjaxFormCode` in src/lib/utils/formEmbedUtils.ts)
   * because its markup is in the DOM before the script tag runs.
   */
  const TURNSTILE_SCRIPT_URL =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

  /**
   * Which language the Turnstile challenge should speak.
   *
   * Cloudflare's default is "auto", which follows the *visitor's browser*, not
   * the page. A visitor whose browser is set to German therefore gets a German
   * challenge inside an English form on an English site, which is confusing for
   * them and looks broken to the site's owner.
   *
   * The page's own `lang` is the better signal: it is the language the
   * surrounding labels and help text are written in, so matching it keeps the
   * widget consistent with everything around it. Falls back to "auto" when the
   * document declares nothing, which is the previous behaviour.
   *
   * Cloudflare accepts "auto", a two-letter ISO 639-1 code ("en", "de") and
   * regional variants ("en-US", "pt-BR"), so a well-formed `lang` can be passed
   * through unchanged. Anything it does not recognise it falls back on itself.
   */
  function turnstileLanguage(): string {
    try {
      const declared = document.documentElement.getAttribute("lang");
      return (declared || "").trim() || "auto";
    } catch {
      return "auto";
    }
  }

  /**
   * The payload key the submit endpoint reads the challenge token from.
   *
   * `src/pages/api/forms/[id].ts` accepts `cf-turnstile-response` first and
   * `cf_turnstile_response` as a fallback; this is the hyphenated one, which
   * is also the name Cloudflare gives its own hidden input, so HTML mode and
   * the React embed send the same key. A rename on either side rejects every
   * submission on every protected form and says nothing, which is why the key
   * is a named constant here rather than a literal at the call site: it is one
   * half of a two-file invariant.
   */
  const TURNSTILE_TOKEN_KEY = "cf-turnstile-response";

  /** How long to wait for api.js to define `window.turnstile`, in ms. */
  const TURNSTILE_READY_TIMEOUT_MS = 10000;
  const TURNSTILE_POLL_INTERVAL_MS = 50;

  /**
   * Where this form's challenge has got to, which is what gates the submit.
   *
   * "pending" covers both "the script has not loaded" and "the visitor has not
   * passed it yet"; the visitor cannot act differently on those two, and the
   * widget itself shows which one it is. "error" and "expired" are separated
   * because they need different sentences: one asks for a reload, the other
   * asks for another go at the check that is still on screen.
   *
   * "refreshing" and "expired" are the same fact about the token and two
   * different facts about what happens next, which is why they are not one
   * state. A dead token that we managed to reset has a replacement coming, so
   * the visitor waits; one we could not reset has nothing coming, so the
   * visitor has to act. Telling them to complete a check that is about to
   * replace itself, or to wait for a replacement that will never arrive, are
   * both wrong in a way that reads as a broken form.
   */
  type TurnstileStatus =
    | "pending"
    | "solved"
    | "refreshing"
    | "expired"
    | "error";

  /** The slice of the Cloudflare API this embed uses. */
  interface TurnstileApi {
    render: (el: HTMLElement, options: Record<string, any>) => string | void;
    remove?: (widgetId: string) => void;
    reset?: (widgetId?: string) => void;
    getResponse?: (widgetId?: string) => string | undefined;
  }

  function readTurnstileApi(): TurnstileApi | null {
    const api = (window as any).turnstile;
    return api && typeof api.render === "function"
      ? (api as TurnstileApi)
      : null;
  }

  /**
   * Load api.js (once per page) and hand back the API it defines.
   *
   * Goes through the shared `loadScript`, which de-dupes on what is in the DOM,
   * so two forms on one page share a single request. That de-dupe is also why
   * the wait below exists: the second form's `loadScript` returns the moment it
   * sees the first form's tag, which is usually before that tag has finished
   * loading, so `window.turnstile` is not there yet. Poll for it rather than
   * assume, and give up loudly rather than hang.
   */
  async function loadTurnstileApi(): Promise<TurnstileApi> {
    const alreadyLoaded = readTurnstileApi();
    if (alreadyLoaded) return alreadyLoaded;

    await loadScript(TURNSTILE_SCRIPT_URL);

    const deadline = Date.now() + TURNSTILE_READY_TIMEOUT_MS;
    for (;;) {
      const api = readTurnstileApi();
      if (api) return api;
      if (Date.now() >= deadline) {
        throw new Error(
          `Turnstile did not define window.turnstile within ${TURNSTILE_READY_TIMEOUT_MS}ms`,
        );
      }
      await new Promise((resolve) =>
        setTimeout(resolve, TURNSTILE_POLL_INTERVAL_MS),
      );
    }
  }

  /**
   * This form's challenge token, or "" when the visitor has not solved it.
   *
   * `getResponse(widgetId)` is the documented read and it is scoped to one
   * widget, which is the point: called with no argument it answers for
   * whichever widget rendered first, so on a page with two embeds both forms
   * would send the same token and the second submission would be rejected as
   * a replay. When the render call handed back no id, fall back to the hidden
   * input Cloudflare writes into the container, which is scoped by being
   * inside this form's own host element.
   */
  function readTurnstileToken(
    widgetId: string | null,
    host: HTMLElement | null,
  ): string {
    const api = readTurnstileApi();
    if (api && widgetId && typeof api.getResponse === "function") {
      try {
        const token = api.getResponse(widgetId);
        if (typeof token === "string") return token;
      } catch {
        // A widget that has already been removed throws rather than
        // answering. Fall through to the input, then to "".
      }
    }
    const input = host?.querySelector(
      `input[name="${TURNSTILE_TOKEN_KEY}"]`,
    ) as HTMLInputElement | null;
    return input?.value || "";
  }

  /**
   * Execute the <script> tags that came with injected HTML.
   *
   * innerHTML never runs scripts, so every script tag in the fetched markup is
   * inert. Remove each one and load it properly: `src` scripts go through
   * loadScript (which de-dupes on what is actually in the DOM, so removing the
   * dead tag first is what makes that de-dupe correct), inline scripts are
   * re-created so the browser evaluates them.
   *
   * A script that fails to load must not take the form down with it. The
   * markup is already injected and usable at this point, and the Cloudflare
   * challenge script is a third-party request that can fail on its own. Log
   * and carry on; the caller's init check reports the consequence.
   */
  async function activateInjectedScripts(
    container: HTMLElement,
    config: EmbedConfig,
  ): Promise<void> {
    const injected = Array.from(container.querySelectorAll("script"));
    if (injected.length === 0) return;

    const pending: Promise<void>[] = [];

    injected.forEach((el) => {
      const src = el.getAttribute("src");
      const inlineCode = el.textContent || "";
      el.remove();

      if (src) {
        pending.push(
          loadScript(src).catch((error) => {
            console.error(`KirokuEmbed: Failed to load ${src}`, error);
          }),
        );
        return;
      }

      if (inlineCode.trim()) {
        try {
          const replacement = document.createElement("script");
          replacement.textContent = inlineCode;
          document.head.appendChild(replacement);
        } catch (error) {
          console.error("KirokuEmbed: Failed to run an inline script", error);
        }
      }
    });

    await Promise.all(pending);

    if (config.debug) {
      console.log(
        `KirokuEmbed: Activated ${injected.length} injected script tag(s)`,
      );
    }
  }

  /**
   * HTML Mode: Fetch HTML and inject
   */
  async function embedHtmlMode(
    config: EmbedConfig,
    container: HTMLElement,
  ): Promise<void> {
    const embedUrl = `${config.apiUrl}/api/forms/${config.formId}/embed?mode=html&css=${config.css}`;

    if (config.debug) {
      console.log(`KirokuEmbed: Fetching HTML from ${embedUrl}`);
    }

    try {
      const response = await fetch(embedUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      /*
       * A popup form placed with the HTML-mode snippet opens as a popup.
       *
       * The dashboard hands out `data-kiroku-mode="html"` (generateScriptEmbedHtml
       * in src/lib/utils/formEmbedUtils.ts), and the popup engine lives only in
       * embedReactMode, so a form switched to popup rendered inline while the
       * dashboard said it would open as a popup
       * (docs/wordpress-popup-builders_research.md §1.3). The server marks a
       * popup form's HTML response with `X-Kiroku-Popup: 1`, so this costs a
       * second request only for popup forms, and it also fixes every snippet
       * already pasted on a customer's site. `data-kiroku-popup="false"` keeps
       * a placement inline on purpose.
       */
      if (
        !config.popupOptOut &&
        response.headers.get("X-Kiroku-Popup") === "1"
      ) {
        if (config.debug) {
          console.log(
            "KirokuEmbed: Form is set to open as a popup; switching to the popup renderer",
          );
        }
        await embedReactMode({ ...config, mode: "react" }, container);
        return;
      }

      const html = await response.text();

      // Inject HTML
      container.innerHTML = html;

      // A <script> that innerHTML put in the DOM never executes (HTML spec),
      // but it is still findable by a selector. The `?mode=html` payload
      // carries two of them: kiroku-form.js, which binds the submit handler,
      // and the Cloudflare challenge script when Turnstile is on
      // (generateAjaxFormCode in src/lib/utils/formEmbedUtils.ts). Leaving the
      // dead tags in place made loadScript's `script[src="..."]` de-dupe treat
      // "present in the DOM" as "already loaded", so it returned without
      // loading anything, window.KirokuForms.init never existed, no submit
      // listener was bound, and the browser fell back to a native POST. The
      // generated <form> has no `action`, so that POST hit the host page's own
      // URL and rendered the customer's 404. Take the dead tags out and load
      // them for real.
      await activateInjectedScripts(container, config);

      // Load CSS if external mode
      if (config.css === "external") {
        await loadCSS(config.apiUrl);
      }

      // Load and initialize kiroku-form.js for AJAX handling. Usually a no-op
      // by now: activateInjectedScripts has already loaded this exact URL. It
      // still matters when the payload carried no script tag, or when the
      // host page overrode data-kiroku-api-url so the two URLs differ
      // (kiroku-form.js is idempotent on re-execution, by its own contract).
      const formScriptUrl = `${config.apiUrl}/js/kiroku-form.js?v=1.0.0`;
      await loadScript(formScriptUrl);

      // The built-in visitor check's widget (task 2.2). `generateHtmlFormCode`
      // draws the <altcha-widget> tag but, like generateTurnstileHtml's own
      // caller, emits no script tag of its own; `generateAjaxFormCode` does
      // carry one, already handled by activateInjectedScripts above, and
      // loadAltchaWidget's own de-dupe makes calling it again here harmless.
      // Checking the DOM for the tag, rather than re-deriving the form's
      // effective check, works for markup from either generator.
      if (container.querySelector("altcha-widget")) {
        await loadAltchaWidget("builtin", config.apiUrl);
      }

      // Wait for KirokuForms to be available
      if (
        typeof window.KirokuForms !== "undefined" &&
        window.KirokuForms.init
      ) {
        // Find the form element and initialize it
        const formElement = container.querySelector(
          "form.kiroku-embed-form",
        ) as HTMLFormElement;
        if (formElement) {
          window.KirokuForms.init(formElement);
          if (config.debug) {
            console.log("KirokuEmbed: Initialized form handlers");
          }
        }
      } else {
        // Without init there is no submit listener, and the form silently
        // degrades to a native POST that leaves the customer's page. Say so:
        // this used to fail with nothing in the console at all.
        console.error(
          `KirokuEmbed: kiroku-form.js did not define window.KirokuForms.init, so the form is not bound and would submit natively. Check that ${formScriptUrl} loads.`,
        );
      }

      if (config.debug) {
        console.log("KirokuEmbed: HTML mode injection complete");
      }
    } catch (error) {
      console.error("KirokuEmbed: Failed to load form", error);
      container.innerHTML = `
        <div style="padding: 1rem; border: 1px solid #dc2626; border-radius: 0.375rem; background: #fef2f2; color: #dc2626;">
          <strong>Error loading form</strong>
          <p style="margin: 0.5rem 0 0 0; font-size: 0.875rem;">Unable to load the form. Please try again later.</p>
        </div>
      `;
    }
  }

  /**
   * React Mode: Fetch JSON and render with React
   */
  // ============================================================================
  // URL PREFILL (inlined from src/lib/utils/urlPrefill.ts)
  // ============================================================================
  // The embed bundle must stay free of @/lib imports (src/lib/types.ts:116-118),
  // so the ~15-line extractor is copied here rather than imported. The prefix and
  // filter mirror extractPrefillValues; tests/guard/embed-built-has-url-prefill.ts
  // pins this copy to URL_PREFILL_PREFIX so it can't drift.
  const URL_PREFILL_PREFIX = "kf_";

  function extractPrefillValues(
    searchParams: URLSearchParams,
    validFieldNames: Set<string>,
  ): Record<string, string> {
    const result: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (!key.startsWith(URL_PREFILL_PREFIX)) return;
      const fieldName = key.slice(URL_PREFILL_PREFIX.length);
      if (!fieldName) return;
      if (!validFieldNames.has(fieldName)) return;
      result[fieldName] = value;
    });
    return result;
  }

  async function embedReactMode(
    config: EmbedConfig,
    container: HTMLElement,
    options: { openNow?: boolean; values?: Record<string, string> } = {},
  ): Promise<void> {
    const embedUrl = `${config.apiUrl}/api/forms/${config.formId}/embed?mode=json`;

    if (config.debug) {
      console.log(`KirokuEmbed: Fetching JSON from ${embedUrl}`);
    }

    try {
      // Fetch form configuration
      const response = await fetch(embedUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.message || "Failed to fetch form data");
      }

      const formData = result.data;

      // docs/spam-defence_plan.md task 4.3: every form gets a trap by default
      // now (task 4.2), but `GET /api/forms/[id]/embed?mode=json` still only
      // sends `form.fields` as stored, so a form that never configured its
      // own honeypot arrives with no `type: "honeypot"` entry for the
      // existing `case "honeypot":` render branch (below) to draw. Injected
      // here, once, rather than server-side, so this stays the one file the
      // plan's own task lists for the React-mode renderer; the field then
      // flows through `visibleFields`/`formValues`/submission exactly like
      // any other field, with no further change in this file.
      if (needsDefaultHoneypotField(formData)) {
        const firstStepId =
          formData.isMultiStep && Array.isArray(formData.steps)
            ? [...formData.steps].sort((a: any, b: any) => a.order - b.order)[0]
                ?.id
            : null;
        const baseFields = Array.isArray(formData.fields)
          ? formData.fields
          : [];
        formData.fields = [
          ...baseFields,
          buildDefaultHoneypotField({
            formId: formData.id,
            stepId: firstStepId ?? null,
            order: baseFields.length,
          }),
        ];
      }

      // Plan 3.5 of docs/guided-shopping_plan.md: keep the latest
      // VeritaMetrics intent tier for the life of this embed instance,
      // independent of whether a popup is configured, so an inline form on a
      // page running VeritaMetrics can carry it too. `armIntent` in
      // ./popupTriggers listens for the identical VM_INTENT_EVENT to trigger
      // a popup; this is a second, standalone listener that only records the
      // value. Absent when the site does not run VeritaMetrics, since the
      // event then never fires. Whether it reaches `_meta` is a submit-time
      // decision (below), gated on tracking consent.
      //
      // Behind the tracking posture on both sides (CLAUDE.md §13): attached
      // only when consent allows at load (the GDPR half), and taken off, with
      // the remembered tier dropped, the first time a submit-time check finds
      // consent withdrawn (the CCPA half; see stopIntentListener's caller).
      let latestIntentTier: string | undefined;
      const onIntent = (event: Event) => {
        const detail = (event as CustomEvent).detail;
        if (detail && typeof detail.tier === "string" && detail.tier) {
          latestIntentTier = detail.tier;
        }
      };
      if (
        resolveTrackingConsent(trackingConsentFromSettings(formData.settings))
          .allowed
      ) {
        document.addEventListener(VM_INTENT_EVENT, onIntent);
      }
      const stopIntentListener = () => {
        document.removeEventListener(VM_INTENT_EVENT, onIntent);
        latestIntentTier = undefined;
      };

      // The paid-tier decision, made server-side (see the branding node in the
      // render tree below). `!== false` on purpose: absent, null or any
      // non-boolean means the server did not say the owner has white-label, and
      // the safe default there is to show the attribution.
      const showBranding = formData.showBranding !== false;

      // Popup display mode. Inline (untouched default) unless one of three opts
      // in: form.settings.popup.enabled, a per-placement data-kiroku-trigger
      // override, or a programmatic open() (options.openNow). When enabled the
      // form renders into a hidden host and the modal opens on the trigger
      // (exit/delay/scroll/click/visible) or immediately for open(), capped by
      // the frequency window on the armed path only. The placement override
      // wins over settings.popup.trigger. Keyed by formId so multiple popup
      // embeds on one page keep independent show/convert state.
      const popup = formData.settings?.popup;
      const openNow = options.openNow === true;
      const popupEnabled =
        openNow || !!config.triggerOverride || !!(popup && popup.enabled);
      const popupTrigger = popupEnabled
        ? createPopupTrigger({
            // Each trigger carries its own parameter (scroll depth, intent
            // tier, or the two halves of an AND pair), so the settings object
            // is converted to a descriptor rather than passed as a bare name:
            // a bare `"scroll"` has no threshold and would arm nothing.
            trigger:
              config.triggerOverride ??
              popupSettingsToTrigger({
                trigger: popup?.trigger,
                delayMs: popup?.delayMs,
                scrollPercent: popup?.scrollPercent,
                intentLevel: popup?.intentLevel,
                selector: popup?.selector,
                and: popup?.and,
              }) ??
              [],
            // What a touch visitor gets instead, when the owner has set one
            // (task 3.1). "same", absent or unparseable converts to
            // `undefined`, which is no override: a touch visitor arms
            // `trigger` above unchanged, same as a form saved before this
            // setting existed.
            mobileTrigger:
              mobilePopupSettingsToTrigger(popup?.mobileTrigger) ?? undefined,
            delayMs: popup?.delayMs,
            // Which visitors the popup is for. Absent = all of them, so a form
            // stored before this setting existed keeps opening for everyone.
            devices: popup?.devices,
            // Which pages of the host site it is for. Absent = all of them,
            // so a form stored before this setting existed keeps opening
            // wherever the snippet is, and the engine reads no `location`.
            pages: popup?.pages,
            // Who the popup is for beyond devices/pages: campaign, referrer,
            // new/returning, a schedule, a country list (task 3.1 of
            // docs/popup-formats-and-targeting_plan.md). Absent = every
            // visitor devices/pages already allow.
            targeting: popup?.targeting,
            // The visitor's country, resolved server-side by the embed
            // endpoint from the x-vercel-ip-country edge header, and present
            // only when the form declares a countries rule (task 3.2). The
            // engine has no client-side signal for geography; when this is
            // absent, a countries rule fails closed rather than guessing.
            visitorCountry: formData.visitor?.country ?? undefined,
            frequencyDays: popup?.frequencyDays,
            // How the `exit` trigger reads a departing cursor: the top-edge
            // band it has to leave through, and how long to wait before
            // believing it. Forwarded verbatim, absent included: the signals
            // module applies its own defaults, so a form saved before these
            // settings existed behaves exactly as it did.
            exitSensitivityPx: popup?.exitSensitivityPx,
            mouseLeaveDelayMs: popup?.mouseLeaveDelayMs,
            // How long before exit/scroll/visible/intent are allowed to fire
            // (task 2.3). Forwarded verbatim, absent included: the engine
            // falls back to its own 15000ms default, so a form saved before
            // this setting existed keeps the guard it always had.
            minTimeOnPageMs: popup?.minTimeOnPageMs,
            // Total times this visitor may ever see it. Absent = no limit, so a
            // form stored before this setting existed keeps re-showing on the
            // frequency window alone.
            maxShows: popup?.maxShows,
            // An explicit decline (rendered in a later phase) suppresses re-show
            // for its own, longer window; wire the setting through now so the
            // engine honors it as soon as the decline control calls markDeclined.
            declineSuppressDays: popup?.decline?.suppressDays,
            // Where the visitor's own consent banner records its answer, named
            // by the customer in the settings panel. Absent on most forms,
            // which resolves to the default posture and counts as it does
            // today. The engine reads it once, before it attaches anything, and
            // every beacon below asks the same resolved answer.
            //
            // Read through the shared helper rather than `popup?.trackingConsent`
            // because task 0.1 promoted the field to `settings.trackingConsent`
            // and both locations are still read. LiveForm calls the same helper,
            // so the two renderers cannot disagree about where the answer lives.
            //
            // `gpc: true` is appended last, exactly as LiveForm.tsx does, so
            // nothing stored can turn off Global Privacy Control: it is the
            // visitor's own browser signal, resolveTrackingConsent treats it
            // as a source that can only deny, and it must outrank a banner
            // that granted.
            consent: {
              ...trackingConsentFromSettings(formData.settings),
              gpc: true,
            },
            // A visitor who turns tracking off with the page still open. The
            // engine watches the signal above and tears itself down on a no;
            // this is only the debug line, which is where the reason has to go
            // until the dashboard has somewhere to show it (CLAUDE.md §13).
            onTrackingRevoked: (resolution) => {
              if (config.debug) {
                console.log(`KirokuEmbed: ${resolution.reason}`);
              }
            },
            formId: config.formId,
          })
        : null;

      /**
       * The one gate on popup analytics (task 4.3, decision 0.2 of
       * docs/consent-management_plan.md). All three events go through here
       * rather than calling sendViewBeacon directly, so the check cannot be
       * forgotten at a fourth call site.
       *
       * Only the counting layer is gated. The modal still opens, the
       * `kiroku:popup:*` re-show record is still written, and the submission
       * itself is untouched: a visitor whose banner said no still gets the
       * popup they were shown, and their form still submits.
       */
      function sendPopupBeacon(body: Record<string, any>): void {
        if (popupTrigger && !popupTrigger.consent().allowed) {
          if (config.debug) {
            console.log(
              `KirokuEmbed: ${body.event} not counted. ${popupTrigger.consent().reason}`,
            );
          }
          return;
        }
        sendViewBeacon(config, body);
      }

      // Explicit decline ("Not right now"): a stronger signal than a passive
      // close. Per the plan order — close the modal, record the decline
      // (starts the longer suppress window), notify the host page via a
      // document `kiroku:declined` event, and send a best-effort beacon to
      // the view endpoint. Each side effect is guarded so a failure (e.g. a
      // rejected beacon) never blocks the close, keeping shown / converted /
      // declined three distinct signals.
      //
      // Lives in the outer scope (moved here from inside KirokuFormComponent
      // by plan task 3.2), because the offer screen's "No" button reaches it
      // through showModal's `onNo`, and showModal is a sibling of the
      // component, not a descendant of it.
      const handleDecline = () => {
        closeModal();
        if (popupTrigger) popupTrigger.markDeclined();
        try {
          document.dispatchEvent(
            new CustomEvent("kiroku:declined", {
              detail: { formId: config.formId },
            }),
          );
        } catch {
          /* event dispatch must not block the close */
        }
        sendPopupBeacon({
          event: "declined",
          // Same expression the "shown"/"converted" beacons use, so a
          // decline buckets under the trigger that actually showed the
          // popup rather than landing untriggered in getPopupFunnel.
          trigger: config.triggerOverride ?? popup?.trigger,
          url: window.location.href,
          referrer: document.referrer || "",
          timestamp: new Date().toISOString(),
        });
      };

      // Load CSS
      await loadCSS(config.apiUrl);

      // Create a minimal LiveForm-like component. Preact (compat) is bundled
      // into this script, so there is no external React CDN fetch; useState/
      // useMemo/useRef come from the preact/hooks import at the top. No
      // createRoot: Preact's render(vnode, container) is React-17-style and the
      // render code below falls back to it. preact/compat keeps className,
      // onChange, htmlFor, and controlled-input semantics React-compatible.
      const React: any = { createElement };
      const ReactDOM: any = { render };

      /**
       * Plan 5.1: the guided-shopping funnel's own visitor experience,
       * mirroring `GuidedFunnelRenderer.tsx` (LiveForm's counterpart) class
       * for class -- same DOM shape, same `resultScreen.ts` content builder,
       * so the two renderers cannot come to show a visitor two different
       * things for the same answers. Written with `createElement` rather
       * than JSX because this file's esbuild config (scripts/build-embed.mjs)
       * has no JSX loader configured for it, matching every other component
       * in this file.
       */
      const GuidedFunnelWidget = ({ funnel }: { funnel: FunnelConfig }) => {
        const compiled = useMemo(() => compileFunnel(funnel), [funnel]);
        // Plan 5.2, §3.14 rule 5: a step count is only honest when every
        // path through the flow asks the same number of questions.
        const totalIfFixed = useMemo(
          () => fixedPathLength(compiled.fields),
          [compiled],
        );
        // Plan 5.6: Sandler's up-front contract, screen 1, mirroring
        // GuidedFunnelRenderer.tsx.
        const upFront = useMemo(
          () => buildUpFrontScreen(funnel, compiled),
          [funnel, compiled],
        );
        // Plan 5.3: the same link on every screen, computed once per config.
        const contactLink = useMemo(
          () => ownerContactLink(funnel.ownerContact),
          [funnel],
        );
        // Plan 5.6: screen 1 gates the first question behind a "Start"
        // click, mirroring GuidedFunnelRenderer.tsx.
        const [started, setStarted] = useState(false);
        const [answers, setAnswers] = useState<Record<string, unknown>>({});
        const [contactValue, setContactValue] = useState("");
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [submitError, setSubmitError] = useState<string | null>(null);
        const [submitted, setSubmitted] = useState(false);
        // Plan 5.7: "Email me this summary", mirroring
        // GuidedFunnelRenderer.tsx -- independent of the contact block
        // above (needsContact), since the B2B seed's own offer is `book`
        // and never sets needsContact.
        const [summaryEmailValue, setSummaryEmailValue] = useState("");
        const [summaryEmailStatus, setSummaryEmailStatus] = useState<
          "idle" | "sending" | "sent" | "error"
        >("idle");
        // Plan 5.4: which concern's answer is expanded under the current
        // question, if any. Local UI state only, mirroring
        // GuidedFunnelRenderer.tsx.
        const [openConcernId, setOpenConcernId] = useState<string | null>(null);
        // Plan 5.5: every concern label opened so far this visit,
        // accumulated across questions and sent with the final submission.
        // Needs no consent to record -- the visitor chose to submit it
        // (CLAUDE.md §13) -- unlike the guided_concern_opened beacon below.
        const [openedConcernLabels, setOpenedConcernLabels] = useState<
          string[]
        >([]);
        const autoSubmittedRef = useRef(false);

        /**
         * Plan 5.5: the guided_concern_opened beacon, sent once per concern
         * opened (not on collapse), gated by tracking consent client-side --
         * the same gate `sendPopupBeacon` applies to the three popup events.
         * Best-effort: a rejected or throwing beacon must never interrupt
         * the visitor.
         */
        const recordConcernOpened = (label: string) => {
          if (
            !resolveTrackingConsent(
              trackingConsentFromSettings(formData.settings),
            ).allowed
          ) {
            return;
          }
          try {
            fetch(
              `${config.apiUrl}/api/forms/${formData.id}/interaction-event`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  eventType: "guided_concern_opened",
                  label,
                  referrer: document.referrer || "",
                  url: window.location.href,
                  timestamp: new Date().toISOString(),
                  userAgent: navigator.userAgent,
                }),
                keepalive: true,
              },
            ).catch(() => {});
          } catch {
            /* a failed analytics beacon must not block the interaction */
          }
        };

        /**
         * Plan 7.1: `form_step_view` / `form_step_complete` for a guided
         * funnel's questions, mirroring GuidedFunnelRenderer.tsx (see its own
         * comment for why this had no emitter at all before: `LiveForm`
         * skips its own step-tracking hooks for a guided funnel, and neither
         * React renderer of one called this beacon). No preview/trackView
         * gate here -- unlike the React component, this script only ever
         * runs on a real embed, never a dashboard preview. `field.order` is
         * the field's fixed position in `compiled.fields`
         * (`compileFunnel` builds it from `plan.order`), so it numbers a
         * question the same way for every visitor regardless of branch.
         */
        const postStepEvent = (
          eventType: "form_step_view" | "form_step_complete",
          field: { id: string; name: string; label: string; order: number },
        ) => {
          if (
            !resolveTrackingConsent(
              trackingConsentFromSettings(formData.settings),
            ).allowed
          ) {
            return;
          }
          try {
            fetch(`${config.apiUrl}/api/forms/${formData.id}/step-event`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                eventType,
                stepIndex: field.order,
                totalSteps: compiled.fields.length,
                stepId: field.id,
                stepTitle: field.label,
                referrer: document.referrer || "",
                userAgent: navigator.userAgent,
              }),
              keepalive: true,
            }).catch(() => {});
          } catch {
            /* a failed analytics beacon must not block the interaction */
          }
        };

        const visibleFields = compiled.fields.filter((field) =>
          evaluateFieldVisibility(field, answers, compiled.fields),
        );
        const nextField = visibleFields.find(
          (field) => answers[field.name] == null || answers[field.name] === "",
        );
        const screenResult = nextField
          ? null
          : buildResultScreen(funnel, answers, formData.name);

        const submit = async (
          extra: Record<string, unknown>,
          silent: boolean,
        ) => {
          setIsSubmitting(true);
          setSubmitError(null);
          try {
            const payload: Record<string, unknown> = {
              ...answers,
              ...extra,
              _meta: buildClientMeta({
                search: window.location.search,
                referrer: document.referrer || undefined,
                source: "kiroku-guided-funnel",
                version: VERSION,
                captureMarketing: !!formData.settings?.captureMetadata,
                concernsOpened: openedConcernLabels,
              }),
            };
            const response = await fetch(
              `${config.apiUrl}/api/forms/${formData.id}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify(payload),
              },
            );
            const result = await readApiEnvelope(response);
            if (!result?.success) {
              if (!silent) {
                setSubmitError(result?.message || "Submission failed");
              }
              return;
            }
            if (!silent) setSubmitted(true);
          } catch {
            if (!silent) {
              setSubmitError("Submission failed. Please try again.");
            }
          } finally {
            setIsSubmitting(false);
          }
        };

        /**
         * Plan 5.7: sends the visitor their own copy of the result screen,
         * mirroring GuidedFunnelRenderer.tsx. Never touches `submit` or its
         * state -- the server recomputes the recap from `answers` itself
         * and stores neither the address nor the answers.
         */
        const sendSummaryEmail = async () => {
          setSummaryEmailStatus("sending");
          try {
            const response = await fetch(
              `${config.apiUrl}/api/forms/${formData.id}/guided-summary-email`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: summaryEmailValue,
                  answers,
                }),
              },
            );
            const result = await readApiEnvelope(response);
            setSummaryEmailStatus(result?.success ? "sent" : "error");
          } catch {
            setSummaryEmailStatus("error");
          }
        };

        useEffect(() => {
          if (!screenResult || screenResult.needsContact) return;
          if (autoSubmittedRef.current) return;
          autoSubmittedRef.current = true;
          void submit({}, true);
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [screenResult?.outcome.id, screenResult?.needsContact]);

        // Plan 5.4: closes an expanded concern when the question changes,
        // matching GuidedFunnelRenderer.tsx.
        useEffect(() => {
          setOpenConcernId(null);
        }, [nextField?.id]);

        // Plan 7.1: one form_step_view per question actually shown, gated on
        // `started` because `nextField` is computed above regardless of it --
        // matching GuidedFunnelRenderer.tsx.
        useEffect(() => {
          if (!started || !nextField) return;
          postStepEvent("form_step_view", nextField);
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [started, nextField?.id]);

        const contactLinkNode = contactLink
          ? React.createElement(
              "a",
              {
                href: contactLink.href,
                className: "kiroku-guided-owner-contact",
              },
              contactLink.text,
            )
          : null;

        if (!started) {
          return React.createElement(
            "div",
            {
              className: "kiroku-guided-intro",
              role: "group",
              "aria-label": "Before you start",
            },
            React.createElement(
              "p",
              { className: "kiroku-guided-intro-text" },
              upFront.text,
            ),
            React.createElement(
              "button",
              {
                type: "button",
                className: "kiroku-guided-intro-start",
                onClick: () => setStarted(true),
              },
              "Start",
            ),
            contactLinkNode,
          );
        }

        if (submitted) {
          return React.createElement(
            "div",
            null,
            React.createElement(
              "div",
              {
                className: "kiroku-guided-thanks",
                role: "status",
                style: { color: "#15803d", fontWeight: 500 },
              },
              "Thanks, that's on its way.",
            ),
            contactLinkNode,
          );
        }

        if (nextField) {
          // Same 1-based position rule as GuidedFunnelRenderer.tsx: one
          // answered field is one asked question, and only one field is
          // ever unanswered-and-visible at a time.
          const stepIndex = Object.keys(answers).length + 1;
          const concerns = nextField.concerns ?? [];
          const openConcern =
            concerns.find((c: any) => c.id === openConcernId) ?? null;
          const openConcernActionLink = concernActionLink(openConcern?.action);
          return React.createElement(
            "div",
            {
              className: "kiroku-guided-question",
              role: "group",
              "aria-label": nextField.label,
            },
            totalIfFixed !== null
              ? React.createElement(
                  "p",
                  { className: "kiroku-guided-progress" },
                  `Question ${stepIndex} of ${totalIfFixed}`,
                )
              : stepIndex === 1
                ? React.createElement(
                    "p",
                    { className: "kiroku-guided-progress" },
                    "A few quick questions",
                  )
                : null,
            React.createElement("h2", null, nextField.label),
            nextField.helpText
              ? React.createElement("p", null, nextField.helpText)
              : null,
            React.createElement(
              "div",
              { className: "kiroku-guided-options" },
              ...(nextField.options ?? []).map((option: any) =>
                React.createElement(
                  "button",
                  {
                    key: String(option.value),
                    type: "button",
                    className: "kiroku-guided-option",
                    onClick: () => {
                      postStepEvent("form_step_complete", nextField);
                      setAnswers((prev: Record<string, unknown>) => ({
                        ...prev,
                        [nextField.name]: String(option.value),
                      }));
                    },
                  },
                  option.label,
                ),
              ),
            ),
            concerns.length > 0
              ? React.createElement(
                  "div",
                  { className: "kiroku-guided-concerns" },
                  React.createElement("p", null, "Questions?"),
                  React.createElement(
                    "div",
                    { className: "kiroku-guided-concerns-buttons" },
                    ...concerns.map((concern: any) =>
                      React.createElement(
                        "button",
                        {
                          key: concern.id,
                          type: "button",
                          id: `concern-button-${concern.id}`,
                          "aria-expanded": openConcernId === concern.id,
                          "aria-controls": `concern-answer-${concern.id}`,
                          onClick: () => {
                            const opening = openConcernId !== concern.id;
                            setOpenConcernId(opening ? concern.id : null);
                            if (opening) {
                              setOpenedConcernLabels((prev: string[]) =>
                                withConcernOpened(prev, concern.label),
                              );
                              recordConcernOpened(concern.label);
                            }
                          },
                        },
                        concern.label,
                      ),
                    ),
                  ),
                  openConcern
                    ? React.createElement(
                        "div",
                        {
                          id: `concern-answer-${openConcern.id}`,
                          role: "region",
                          "aria-labelledby": `concern-button-${openConcern.id}`,
                          className: "kiroku-guided-concern-answer",
                        },
                        React.createElement("p", null, openConcern.answer),
                        openConcernActionLink
                          ? React.createElement(
                              "a",
                              {
                                href: openConcernActionLink.href,
                                className: "kiroku-guided-concern-action",
                              },
                              openConcernActionLink.text,
                            )
                          : null,
                        React.createElement(
                          "button",
                          {
                            type: "button",
                            onClick: () => setOpenConcernId(null),
                          },
                          "Back to the question",
                        ),
                      )
                    : null,
                )
              : null,
            contactLinkNode,
          );
        }

        const screen = screenResult!;
        return React.createElement(
          "div",
          { className: "kiroku-guided-result" },
          React.createElement(
            "div",
            {
              className: "kiroku-guided-outcome",
              "data-outcome-id": screen.outcome.id,
            },
            screen.recap.length > 0
              ? React.createElement(
                  "div",
                  { className: "kiroku-guided-recap" },
                  React.createElement("h3", null, "Here's what you told us"),
                  React.createElement(
                    "ul",
                    null,
                    ...screen.recap.map((item) =>
                      React.createElement(
                        "li",
                        { key: item.question },
                        `${item.question} `,
                        React.createElement("strong", null, item.answer),
                      ),
                    ),
                  ),
                )
              : null,
            screen.reframe
              ? React.createElement(
                  "p",
                  { className: "kiroku-guided-reframe" },
                  screen.reframe,
                )
              : null,
            screen.consequence
              ? React.createElement(
                  "p",
                  { className: "kiroku-guided-consequence" },
                  screen.consequence.text,
                  React.createElement(
                    "span",
                    { className: "kiroku-guided-consequence-source" },
                    `Source: ${screen.consequence.source}`,
                  ),
                )
              : null,
            screen.outcomePick
              ? React.createElement(
                  "p",
                  { className: "kiroku-guided-outcome-pick" },
                  screen.outcomePick,
                )
              : null,
            React.createElement("h2", null, screen.outcome.title),
            React.createElement("p", null, screen.recommendation.headline),
            screen.outcome.body &&
              screen.outcome.body !== screen.recommendation.headline
              ? React.createElement("p", null, screen.outcome.body)
              : null,
            screen.hesitationAnswer
              ? React.createElement(
                  "p",
                  { className: "kiroku-guided-hesitation" },
                  screen.hesitationAnswer,
                )
              : null,
            screen.nextSteps.length > 0
              ? React.createElement(
                  "ol",
                  { className: "kiroku-guided-next-steps" },
                  ...screen.nextSteps.map((step) =>
                    React.createElement("li", { key: step }, step),
                  ),
                )
              : null,
            !screen.needsContact &&
              screen.outcome.cta &&
              outcomeCtaHref(screen.outcome.cta)
              ? React.createElement(
                  "a",
                  {
                    className: "kiroku-guided-cta",
                    href: outcomeCtaHref(screen.outcome.cta),
                    rel: "noopener noreferrer",
                  },
                  screen.outcome.cta.label,
                )
              : null,
          ),
          screen.needsContact
            ? React.createElement(
                "div",
                { className: "kiroku-guided-contact" },
                React.createElement(
                  "label",
                  { htmlFor: "guided-contact-email" },
                  screen.contactPrompt,
                ),
                React.createElement("input", {
                  id: "guided-contact-email",
                  name: "email",
                  type: "email",
                  required: true,
                  value: contactValue,
                  onChange: (e: any) => setContactValue(e.target.value),
                }),
                screen.recipientLine
                  ? React.createElement(
                      "p",
                      { className: "kiroku-guided-recipient" },
                      screen.recipientLine,
                    )
                  : null,
                React.createElement(
                  "button",
                  {
                    type: "button",
                    disabled: isSubmitting || !contactValue,
                    onClick: () => submit({ email: contactValue }, false),
                  },
                  screen.actionLabel,
                ),
              )
            : null,
          screen.offersSummaryEmail
            ? React.createElement(
                "div",
                { className: "kiroku-guided-summary-email" },
                React.createElement(
                  "label",
                  { htmlFor: "guided-summary-email" },
                  "Email me this summary",
                ),
                React.createElement("input", {
                  id: "guided-summary-email",
                  name: "summaryEmail",
                  type: "email",
                  value: summaryEmailValue,
                  onChange: (e: any) => setSummaryEmailValue(e.target.value),
                }),
                React.createElement(
                  "button",
                  {
                    type: "button",
                    disabled:
                      summaryEmailStatus === "sending" ||
                      summaryEmailStatus === "sent" ||
                      !summaryEmailValue,
                    onClick: sendSummaryEmail,
                  },
                  summaryEmailStatus === "sent" ? "Sent" : "Send me a copy",
                ),
                summaryEmailStatus === "error"
                  ? React.createElement(
                      "p",
                      { className: "kiroku-guided-summary-email-error" },
                      "Could not send that. Check the address and try again.",
                    )
                  : null,
              )
            : null,
          submitError
            ? React.createElement(
                "p",
                { className: "kiroku-guided-error" },
                submitError,
              )
            : null,
          contactLinkNode,
        );
      };

      // The red asterisk beside a required field's label. One definition, two
      // callers: the field-group label, and a lone boolean checkbox's own
      // label, which is the only marker that field can get because the group
      // label is suppressed for it.
      const requiredStar = (key: string) =>
        React.createElement(
          "span",
          {
            key,
            className: "kiroku-required-star",
            style: { color: "#dc2626", marginLeft: "0.25rem" },
          },
          "*",
        );

      // Define the component to handle state
      const KirokuFormComponent = () => {
        // Plan 5.1: a guided-shopping funnel is a completely different
        // visitor experience from the fields/steps this component otherwise
        // renders (see GuidedFunnelWidget above). Branching before any hook
        // below is called, rather than inside the body, keeps every hook's
        // call order identical between renders of one mounted form, whose
        // own `settings.funnel` does not appear or disappear mid-session.
        if (formData.settings?.funnel) {
          return React.createElement(GuidedFunnelWidget, {
            funnel: formData.settings.funnel,
          });
        }

        const [currentStepIndex, setCurrentStepIndex] = useState(0);
        const [isSubmitting, setIsSubmitting] = useState(false);

        // The computed quiz result, once there is one. Null for every form that
        // declares no outcomes, which is all of them today, and those keep the
        // green success message exactly as before (plan 6.4).
        const [quizResult, setQuizResult] = useState<QuizResult | null>(null);

        // The theme's `--kiroku-*` set as inline custom properties, for the
        // form and for the result screen (see themeStyleFrom). Once per mount:
        // formData is fixed for the life of the component.
        const themeStyle = useMemo(
          () => themeStyleFrom(formData.themeVars),
          [],
        );

        // Per-field messages from the server's 422 body, keyed by field name.
        // The submit endpoint has always sent them as `error.details`
        // (src/pages/api/forms/[id].ts:223); the embed read `error.message`
        // alone, so every rejection rendered as the one sentence "Submission
        // validation failed." and the user had to guess which field to change.
        const [fieldErrors, setFieldErrors] = useState<Record<string, string>>(
          {},
        );

        // Initialize state with default values and hidden field values
        const [formValues, setFormValues] = useState(() => {
          const initial: any = {};
          const fieldNames = new Set<string>();
          if (formData.fields && Array.isArray(formData.fields)) {
            formData.fields.forEach((field: any) => {
              fieldNames.add(field.name);
              // Initialize all fields to null (instead of undefined)
              // so they are explicitly sent in the JSON payload.
              initial[field.name] = null;

              // Apply defaults if present
              if (
                field.defaultValue !== undefined &&
                field.defaultValue !== null
              ) {
                initial[field.name] = field.defaultValue;
              } else if (field.type === "hidden" && field.value !== undefined) {
                // Capture hardcoded values for hidden fields
                initial[field.name] = field.value;
              }
            });
          }

          // Layer kf_-prefixed URL params over defaults/hidden values (URL wins),
          // filtered to known field names. Guarded so a missing location
          // (SSR/jsdom) or malformed query string never breaks the render.
          try {
            const search =
              typeof window !== "undefined" && window.location
                ? window.location.search
                : "";
            if (search) {
              Object.assign(
                initial,
                extractPrefillValues(new URLSearchParams(search), fieldNames),
              );
            }
          } catch {
            // Total over URLSearchParams; a bad query string is a no-op.
          }

          // Explicit open(formId, { values }) override wins over URL and
          // defaults, filtered to known fields so no phantom keys are submitted.
          if (options.values) {
            Object.entries(options.values).forEach(([name, value]) => {
              if (fieldNames.has(name)) initial[name] = value;
            });
          }

          return initial;
        });

        const formRef = useRef<HTMLFormElement | null>(null);

        /**
         * Task 1.1 of docs/popup-triggers-and-funnel_plan.md: the popup path
         * never reported `form_started`. This component is the embed's own
         * "minimal LiveForm-like" renderer (see the comment at its
         * definition) -- a third implementation, parallel to LiveForm.tsx
         * and kiroku-form.ts, which never got the focus beacon the other two
         * carry. Mirrors LiveForm.tsx's trackFieldFocus: once per view, on
         * the first focus of one of the owner's own fields, gated on the
         * tracking-consent posture because this renderer (unlike
         * kiroku-form.ts) has formData.settings in reach. No field name, no
         * value: which field somebody happened to click first says nothing
         * about drop-off.
         */
        const startTrackedRef = useRef(false);
        const trackFormStarted = (event: { target: EventTarget | null }) => {
          if (startTrackedRef.current) return;
          if (!isAnsweringControl(event.target)) return;
          if (
            !resolveTrackingConsent(
              trackingConsentFromSettings(formData.settings),
            ).allowed
          ) {
            return;
          }
          startTrackedRef.current = true;

          fetch(`${config.apiUrl}/api/forms/${formData.id}/interaction-event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              eventType: "form_started",
              referrer: document.referrer || "",
              url: window.location.href,
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent,
            }),
            keepalive: true,
          }).catch(() => {});
        };

        // The fill-time token (docs/spam-network-and-fill-time_plan.md Phase
        // 2, task 2.3). The hosted page mints its own copy server-side
        // (task 2.2); this embed runs in a visitor's browser with no
        // JWT_SECRET, so it asks render-token.ts for one instead, once per
        // mount -- "when a form opens" per the task, not re-fetched per
        // step or re-render. A ref, not state: nothing renders from it, it
        // is only read at submit time, the same shape as altchaPayloadRef
        // below. A failed or slow fetch leaves this null and the submission
        // goes out with no token, exactly like a renderer that never calls
        // this endpoint at all (the plan's "Never do: add a network call to
        // the submit path" governs the submit route, not this one, made at
        // mount and never awaited by handleSubmit).
        const fillTimeTokenRef = useRef<string | null>(null);
        useEffect(() => {
          let abandoned = false;
          fetch(`${config.apiUrl}/api/forms/${formData.id}/render-token`)
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (!abandoned && typeof data?.token === "string") {
                fillTimeTokenRef.current = data.token;
              }
            })
            .catch(() => {
              // Offline, blocked, or the endpoint errored: no token, no
              // block. Matches render-token.ts's own `{ token: null }`
              // fallback for a deployment with no JWT_SECRET.
            });
          return () => {
            abandoned = true;
          };
        }, []);

        // The thank-you that takes a dismissed result's place, inline only
        // (a popup closes instead; see dismissOutcome in the render). A ref
        // rather than state: the messages box is written imperatively
        // (innerHTML, in the submit handler), so a React-owned child in it
        // would be torn out from under the reconciler on the next submit.
        // Written by the layout effect once the form is back in the DOM.
        const dismissedOutcomeMessageRef = useRef<string | null>(null);
        useLayoutEffect(() => {
          const message = dismissedOutcomeMessageRef.current;
          if (quizResult !== null || !message) return;
          dismissedOutcomeMessageRef.current = null;
          const container = formRef.current?.querySelector(
            ".kiroku-form-messages",
          );
          if (!container) return;
          container.innerHTML = "";
          const node = document.createElement("div");
          node.className = "kiroku-form-message kiroku-form-success";
          node.textContent = message;
          container.appendChild(node);
        }, [quizResult]);

        // Handle Change for Logic
        const handleChange = (e: any) => {
          const target = e.target;
          const name = target.name;

          if (!name) {
            console.warn(
              "KirokuEmbed: Input missing 'name' attribute, cannot save value",
              target,
            );
            return;
          }

          let value = target.value;

          // A checkbox that belongs to a group (a field carrying options) is
          // marked data-kiroku-multi by the renderer, and accumulates into an
          // array keyed on the field name. A lone boolean checkbox stays a
          // boolean. The marker exists because the DOM cannot tell them apart:
          // an <input type="checkbox"> with no value attribute reports "on",
          // which is also a legal option value.
          if (target.type === "checkbox") {
            if (target.getAttribute("data-kiroku-multi") === "true") {
              const optionValue = String(target.value);
              const isChecked = target.checked;
              setFormValues((prev: any) => {
                const current = Array.isArray(prev[name]) ? prev[name] : [];
                const next = isChecked
                  ? current.includes(optionValue)
                    ? current
                    : [...current, optionValue]
                  : current.filter((v: any) => v !== optionValue);
                return { ...prev, [name]: next };
              });
              if (config.debug) {
                console.log(
                  `[Kiroku] Field changed: ${name} ${isChecked ? "+" : "-"}`,
                  optionValue,
                );
              }
              return;
            }
            value = target.checked;
          }

          if (config.debug) {
            console.log(`[Kiroku] Field changed: ${name} =`, value);
          }

          setFormValues((prev: any) => ({ ...prev, [name]: value }));
        };

        const isMultiStep =
          formData.isMultiStep && formData.steps && formData.steps.length > 0;

        // Sort steps
        const sortedSteps = useMemo(() => {
          if (!isMultiStep) return [];
          return [...formData.steps].sort(
            (a: any, b: any) => a.order - b.order,
          );
        }, [isMultiStep]);

        const currentStep = isMultiStep ? sortedSteps[currentStepIndex] : null;
        if (isMultiStep && !currentStep) {
          return React.createElement(
            "div",
            { style: { color: "red", padding: "1rem" } },
            "Error: Invalid form steps configuration.",
          );
        }
        const isFirstStep = currentStepIndex === 0;
        const isLastStep =
          !isMultiStep || currentStepIndex === sortedSteps.length - 1;

        // Filter fields
        const visibleFields = useMemo(() => {
          // 1. Get fields for current step
          let stepFields = formData.fields;
          if (isMultiStep) {
            const currentStep = sortedSteps[currentStepIndex];
            if (currentStep) {
              stepFields = formData.fields.filter(
                (f: any) => f.stepId === currentStep.id,
              );
            }
          }

          // 2. Apply Logic Engine
          return stepFields.filter((f: any) =>
            evaluateFieldVisibility(f, formValues, formData.fields),
          );
        }, [isMultiStep, currentStepIndex, formData.fields, formValues]); // Dep on formValues

        /**
         * Send the user to the first field the server rejected.
         *
         * A message the user cannot find is barely better than no message: the
         * three identical retries recorded against form cmt7oz2mq0000sooayeay1mzu
         * are what hunting for it looks like. Moving focus also does the
         * announcing, because a screen reader reads the newly focused control
         * together with the `aria-describedby` message rendered beside it.
         *
         * Order comes from `visibleFields`, which is the order the form is read
         * in. `fieldErrors` is built from a JSON object whose key order is the
         * server's and means nothing to the person looking at the page.
         *
         * A layout effect, so focus lands in the same commit that adds the
         * messages and the browser never paints an unfocused error state. It
         * runs only when `fieldErrors` is replaced, which happens twice per
         * submit: cleared at the start (a no-op here) and set on a rejection.
         */
        useLayoutEffect(() => {
          const form = formRef.current;
          if (!form || Object.keys(fieldErrors).length === 0) return;
          // One pass over the real controls: a field name can contain
          // characters that need escaping in a selector, and CSS.escape is not
          // available everywhere this bundle runs.
          const controls = new Map<string, HTMLElement>();
          form
            .querySelectorAll("input, select, textarea")
            .forEach((el: Element) => {
              const name = el.getAttribute("name");
              if (name && !controls.has(name)) {
                controls.set(name, el as HTMLElement);
              }
            });
          for (const field of visibleFields) {
            // A honeypot is never announced, and a content field renders no
            // control, so both are skipped rather than focused.
            if (field.type === "honeypot") continue;
            if (!fieldErrors[field.name]) continue;
            const control = controls.get(field.name);
            if (!control) continue;
            try {
              control.focus();
            } catch {
              // Focus is an improvement, never a requirement: a detached or
              // unfocusable control must not break the error display.
            }
            return;
          }
        }, [fieldErrors]);

        /**
         * The anti-bot challenge.
         *
         * `turnstileSiteKey` arrives only when the owner ticked the toggle AND
         * this deployment has a key (`resolveTurnstileSiteKey` in
         * src/pages/api/forms/[id]/embed.ts), so plain truthiness here cannot
         * draw a keyless widget. That case is the dangerous one: with
         * TURNSTILE_SECRET_KEY configured, `verifyTurnstileToken` no longer
         * fails open, so a form whose flag is on and whose widget produced no
         * token has every submission rejected as spam
         * (src/pages/api/forms/[id].ts:168).
         */
        const turnstileSiteKey =
          typeof formData.turnstileSiteKey === "string"
            ? formData.turnstileSiteKey.trim()
            : "";
        const turnstileEnabled = !!formData.settings?.turnstileEnabled;
        // The container the widget is drawn into, and the element it was
        // actually drawn into, which are not the same thing: stepping away from
        // the last step and back gives Preact a new node, and the widget has to
        // follow it.
        const turnstileRef = useRef<HTMLDivElement | null>(null);
        const turnstileHostRef = useRef<HTMLElement | null>(null);
        // The rendered widget's id, for the token read at submit time.
        const turnstileWidgetIdRef = useRef<string | null>(null);
        // Whether the challenge has produced a token yet. State, not a ref,
        // because the submit button's disabled attribute is drawn from it.
        const [turnstileStatus, setTurnstileStatus] =
          useState<TurnstileStatus>("pending");

        /**
         * Ask Cloudflare for a new challenge on this form's widget.
         *
         * Called from the three places a token stops being usable, and nowhere
         * else: it expired, the challenge lapsed before it was completed, or
         * it was sent to a server that got far enough to verify it (a token
         * verifies exactly once, so the answer being a rejection rather than an
         * acceptance does not give it back). A reset on a live token
         * throws away something the visitor already earned and can put an
         * interactive challenge back in front of them, so it must never happen
         * on a render, on a timer, or on any other state.
         *
         * The stored id, so a page carrying more than one form resets the
         * widget that needs it rather than whichever Cloudflare considers
         * first.
         *
         * `reset` is optional on the API type because an older api.js, or a
         * proxy that trims the object, may not define it, and Cloudflare owns
         * this widget's lifecycle so a refusal to reset one is not a reason to
         * break the form. Both degrade to what the form did before this
         * existed rather than throwing where nothing would catch it, which is
         * what the returned boolean is for: the caller can tell "a new
         * challenge is coming" from "there is no way back from here" and must
         * not block the submit on the second.
         */
        const resetTurnstileWidget = (): boolean => {
          const api = readTurnstileApi();
          if (!api || typeof api.reset !== "function") return false;
          try {
            api.reset(turnstileWidgetIdRef.current ?? undefined);
            return true;
          } catch {
            return false;
          }
        };

        /**
         * Draw the widget, once, into whichever node is on screen now.
         *
         * A layout effect so the third-party request starts in the same commit
         * that puts the container in the DOM; the body reads no layout. Keyed on
         * `isLastStep` because that is the step the container renders on: a
         * challenge token expires after ~5 minutes, so a multi-step form must
         * not mint one on step 1 and hope it survives to the submit.
         */
        useLayoutEffect(() => {
          if (!turnstileSiteKey) {
            if (turnstileEnabled) {
              // The owner believes this form is protected and it is not.
              // Submissions still go through: POST /api/forms/[id] asks the
              // same question this render just answered
              // (`turnstileChallengeApplies`, src/lib/utils/turnstileWidget.ts)
              // and accepts an unverified submission rather than refusing one
              // it gave nobody the means to pass. This used to say the server
              // would reject every submission, which was true and was the
              // defect. Nobody sees the endpoint's own warning, so say it here
              // too.
              console.error(
                `KirokuEmbed: form ${formData.id} has the anti-bot challenge enabled but its embed config carried no site key, so no challenge is rendered and submissions are accepted without one. Check the form's Allowed Origins in the dashboard.`,
              );
            }
            return;
          }

          const host = turnstileRef.current;
          if (!host) return; // not the step that submits
          if (turnstileHostRef.current === host) return; // already drawn here
          turnstileHostRef.current = host;

          let widgetId: string | null = null;
          let abandoned = false;
          // A redraw (stepping back to page 1 and forward again) mints a new
          // widget, so whatever the last one resolved to is gone with it.
          setTurnstileStatus("pending");

          loadTurnstileApi()
            .then((api) => {
              if (abandoned) return;

              /**
               * The expiry half of the recovery, and the fallback rather than
               * the main path. `refresh-expired: "auto"` below is what normally
               * replaces an expiring token, without the visitor noticing. It
               * covers an expiring token and nothing else, so a challenge that
               * lapsed before it was completed, or a refresh that silently
               * fails, leaves the widget holding a token the server will refuse
               * and the submit gated on it with no way back.
               *
               * Synchronous, inside Cloudflare's own callback, so it lands
               * before any refresh that is already in flight can deliver: the
               * reset must not arrive after a fresh token and wipe it.
               *
               * A widget this effect has already torn down is not ours to
               * touch, hence the `abandoned` check that `resetTurnstileWidget`
               * itself cannot make.
               *
               * The returned boolean is what the two callbacks below tell the
               * visitor with: true means a replacement challenge is on its
               * way and they can wait, false means there is no way back from
               * here and they cannot.
               */
              const resetExpiredWidget = (): boolean => {
                if (abandoned) return false;
                return resetTurnstileWidget();
              };

              /**
               * Say the token is gone, then say whether something is coming.
               *
               * Two calls rather than one, in this order, because a `reset`
               * that resolves synchronously would otherwise be overwritten by
               * a status written after it. "expired" goes up first so the
               * blocked submit always has a sentence under it whatever the
               * reset does; the second call upgrades that to "refreshing"
               * only if the widget has not already solved in between, which
               * is what the updater form checks. Without it a challenge that
               * came straight back would leave the button disabled with a
               * message about renewing that had already finished.
               */
              const reportExpiry = () => {
                setTurnstileStatus("expired");
                if (resetExpiredWidget()) {
                  setTurnstileStatus((current) =>
                    current === "solved" ? current : "refreshing",
                  );
                }
              };

              const rendered = api.render(host, {
                sitekey: turnstileSiteKey,
                theme: "auto",
                // Hidden unless Cloudflare needs the visitor to click.
                appearance: TURNSTILE_APPEARANCE,
                // Follow the page, not the visitor's browser. See
                // turnstileLanguage() for why Cloudflare's default is wrong here.
                language: turnstileLanguage(),
                // Replace the token before it dies, without the visitor doing
                // anything. A token is good for about five minutes and this
                // form sits open while somebody writes an enquiry, so the
                // longer the message the likelier the challenge is stale by
                // the time they press send.
                //
                // "auto" is Cloudflare's documented default, and it is passed
                // explicitly anyway: recovery this form depends on belongs in
                // our source, where it can be read and tested, rather than
                // inherited from a third party's default that can change
                // without us noticing. The alternatives are "manual" (the
                // visitor clicks to refresh) and "never".
                "refresh-expired": "auto",
                // Cloudflare's three outcomes, named as api.js names them.
                // Without these the component never learns the challenge
                // resolved, so the submit button would stay disabled for
                // every visitor who passed it.
                callback: () => {
                  if (!abandoned) setTurnstileStatus("solved");
                },
                "error-callback": () => {
                  if (!abandoned) setTurnstileStatus("error");
                },
                "expired-callback": () => {
                  if (abandoned) return;
                  reportExpiry();
                },
                // A challenge that never finished is not solved. Cloudflare
                // retries by default, so this is the interactive case: the
                // visitor left the widget sitting there. Automatic refresh
                // does not cover this one, it replaces an expiring token and
                // there is no token here, so the reset is the only recovery.
                "timeout-callback": () => {
                  if (abandoned) return;
                  reportExpiry();
                },
              });
              widgetId = typeof rendered === "string" ? rendered : null;
              turnstileWidgetIdRef.current = widgetId;
              if (config.debug) {
                console.log(
                  `[Kiroku] Turnstile widget rendered (${widgetId ?? "no id"})`,
                );
              }
            })
            .catch((error) => {
              // A challenge that never loads must not take the form with it:
              // it stays on screen, says what happened, and refuses to post a
              // submission the server is certain to reject. An ad blocker and
              // a Cloudflare outage both land here.
              if (turnstileHostRef.current === host) {
                turnstileHostRef.current = null;
              }
              if (!abandoned) setTurnstileStatus("error");
              console.error("KirokuEmbed: Turnstile failed to load", error);
            });

          return () => {
            abandoned = true;
            if (turnstileHostRef.current === host) {
              turnstileHostRef.current = null;
            }
            turnstileWidgetIdRef.current = null;
            const api = readTurnstileApi();
            if (api && widgetId && typeof api.remove === "function") {
              try {
                api.remove(widgetId);
              } catch {
                // Cloudflare owns this iframe's lifecycle; a refusal to remove
                // one is not a reason to break the form.
              }
            }
          };
        }, [turnstileSiteKey, turnstileEnabled, isLastStep]);

        /**
         * The self-hosted built-in visitor check (docs/visitor-check-choice_plan.md
         * Phase 2, task 2.2), alongside Turnstile. Unlike Turnstile's div-plus-
         * iframe, the `<altcha-widget>` element is both the container and the
         * widget: `altchaRef` is the widget itself.
         */
        const visitorCheck = effectiveVisitorCheck(formData);
        const altchaRef = useRef<HTMLElement | null>(null);
        // The last solved answer, read directly off the widget's own
        // "statechange" event (`{ payload, state }`, confirmed by reading the
        // installed `altcha` package's bundle) rather than queried from a
        // hidden input, so there is one source of truth for what goes on the
        // payload.
        const altchaPayloadRef = useRef<string | null>(null);
        const [altchaStatus, setAltchaStatus] = useState<
          "pending" | "solved" | "unavailable"
        >("pending");

        useLayoutEffect(() => {
          if (visitorCheck !== "builtin" || !isLastStep) return;
          const host = altchaRef.current;
          if (!host) return;

          let abandoned = false;
          altchaPayloadRef.current = null;
          setAltchaStatus("pending");

          const onStateChange = (event: Event) => {
            if (abandoned) return;
            const detail = (event as CustomEvent).detail as
              | { payload?: string; state?: string }
              | undefined;
            if (detail?.state === "verified") {
              altchaPayloadRef.current = detail.payload ?? null;
              setAltchaStatus("solved");
            } else if (
              detail?.state === "error" ||
              detail?.state === "expired"
            ) {
              // A fresh challenge, not an error page: every verify() call
              // fetches a new one from the widget's `challenge` URL (task
              // 1.2). Silent and automatic, same as Turnstile's own
              // auto-refresh on an expiring token.
              altchaPayloadRef.current = null;
              setTimeout(() => {
                if (!abandoned) (host as any).verify?.();
              }, 1000);
            } else {
              altchaPayloadRef.current = null;
            }
          };
          host.addEventListener("statechange", onStateChange);

          loadAltchaWidget("builtin", config.apiUrl).catch((error) => {
            // The script itself failed to load (blocked, offline, an outage
            // on our own domain). Never strand a real visitor on a check
            // that could not run (the plan's "Never do"): stop gating the
            // submit button and let the submission go out with no answer.
            if (!abandoned) setAltchaStatus("unavailable");
            console.error("KirokuEmbed: ALTCHA widget failed to load", error);
          });

          return () => {
            abandoned = true;
            host.removeEventListener("statechange", onStateChange);
          };
        }, [visitorCheck, isLastStep]);

        /**
         * The submit gate.
         *
         * Only a form that actually draws a widget is gated: without a site
         * key there is nothing for the visitor to complete, so blocking would
         * strand them. `isLastStep` because the widget, and the submit button,
         * are only on that step.
         */
        const turnstileBlocked =
          !!turnstileSiteKey && isLastStep && turnstileStatus !== "solved";
        // "unavailable" (the widget's script failed to load) is deliberately
        // not blocked, same reasoning as turnstileBlocked's own comment: a
        // check that cannot run must not strand the visitor behind it.
        const altchaBlocked =
          visitorCheck === "builtin" &&
          isLastStep &&
          altchaStatus !== "solved" &&
          altchaStatus !== "unavailable";
        /**
         * The sentence shown under the widget. Empty while the challenge is
         * still working, because it usually resolves in a second or two and a
         * line of text that appears and vanishes moves the button under the
         * visitor's finger. A failure gets a message and keeps it.
         *
         * "refreshing" is the one state here that is not a failure: the token
         * died and a replacement was already asked for. It gets a sentence
         * because the submit button is disabled and a disabled control with
         * nothing next to it reads as broken, and it gets a different sentence
         * from "expired" because the two ask opposite things of the visitor.
         * No new line appears or vanishes that did not before: this state is
         * only ever reached from "expired", which already showed one.
         */
        const turnstileMessage =
          turnstileStatus === "error"
            ? TURNSTILE_FAILED_ERROR
            : turnstileStatus === "expired"
              ? TURNSTILE_EXPIRED_ERROR
              : turnstileStatus === "refreshing"
                ? TURNSTILE_REFRESHING_MESSAGE
                : "";
        /**
         * Whether that sentence is reporting something wrong, which is what
         * decides its colour. A renewal in progress is the form working, so
         * painting it in the error red says the opposite of what the words do.
         */
        const turnstileMessageIsFailure = turnstileStatus !== "refreshing";

        const handleNext = () => {
          // Trigger browser validation for current step fields
          if (formRef.current) {
            const inputs = formRef.current.querySelectorAll(
              "input, select, textarea",
            );
            let isValid = true;
            inputs.forEach((input: any) => {
              if (!input.checkValidity()) {
                input.reportValidity();
                isValid = false;
              }
            });
            if (!isValid) return;
          }
          setCurrentStepIndex((prev: number) => prev + 1);
        };

        const handleBack = () => {
          setCurrentStepIndex((prev: number) => prev - 1);
        };

        const handleSubmit = async (e: Event) => {
          e.preventDefault();
          if (isSubmitting) return;

          const form = e.target as HTMLFormElement;

          // Final validation check
          if (!form.checkValidity()) {
            form.reportValidity();
            return;
          }

          // The anti-bot gate, read from this form's own widget
          // (src/pages/api/forms/[id].ts:168). An empty token is not a
          // cheaper "no token": with TURNSTILE_SECRET_KEY configured the
          // server verifies it, fails it, increments the form's spam counter
          // and answers 422, so the visitor is told their submission looked
          // like spam when all that happened is a challenge had not finished.
          // Stop here instead and say which of the three things went wrong.
          // The disabled button below already stops a click and a keyboard
          // submit; this stops `requestSubmit()` and a dispatched event, and
          // it is what catches a token that expired between the two.
          const turnstileToken = turnstileSiteKey
            ? readTurnstileToken(
                turnstileWidgetIdRef.current,
                turnstileRef.current,
              )
            : "";
          if (turnstileSiteKey && !turnstileToken) {
            // The status line under the widget already carries the sentence
            // for a challenge that errored or expired, and it went up the
            // moment that happened rather than waiting for this attempt.
            // Writing it here as well tells the visitor the same thing twice.
            // A challenge that is merely still working has nothing on screen,
            // so that one gets its sentence here.
            if (!turnstileMessage) {
              showFormError(form, TURNSTILE_PENDING_ERROR);
            }
            return;
          }

          // Same reasoning as the Turnstile gate above: the disabled button
          // stops a click, this stops requestSubmit()/a dispatched event,
          // and it is what catches the puzzle still solving between the two.
          if (altchaBlocked) {
            showFormError(form, ALTCHA_WAIT_MESSAGE);
            return;
          }

          setIsSubmitting(true);
          // Last attempt's messages are stale the moment a new one starts.
          setFieldErrors({});

          if (config.debug) {
            console.log("[Kiroku] Submitting form values:", formValues);
          }

          // Construct the final payload including metadata. allowOther: the
          // sentinel represents "Other is picked, nothing typed yet" and
          // must never be submitted -- see src/lib/utils/otherOption.ts.
          const payloadObj: Record<string, any> = {};
          for (const [key, value] of Object.entries(formValues)) {
            payloadObj[key] = stripOtherSentinel(value);
          }
          // Read fresh at submit time, not cached from load, so a mid-session
          // consent withdrawal (CLAUDE.md §13, CCPA/CPRA) is honoured: only
          // whether the tier is transmitted is gated, the same split
          // sendPopupBeacon already applies to the three popup events.
          const intentConsent = resolveTrackingConsent(
            trackingConsentFromSettings(formData.settings),
          );
          if (!intentConsent.allowed) stopIntentListener();
          payloadObj._meta = buildClientMeta({
            search: window.location.search,
            referrer: document.referrer || undefined,
            source: "kiroku-embed-react",
            version: VERSION,
            captureMarketing: !!formData.settings?.captureMetadata,
            intentTier:
              latestIntentTier && intentConsent.allowed
                ? latestIntentTier
                : undefined,
          });

          // Popup-mode marker. popupTrigger is non-null exactly when the form
          // renders inside the modal (settings-driven or programmatic open()),
          // so inline embeds keep their existing _meta. The server copies
          // _meta.submittedVia into the additive submission.created field, which
          // lets webhook consumers attribute the conversion to the popup.
          if (popupTrigger) {
            payloadObj._meta.submittedVia = "popup";
          }

          // The anti-bot token, under the key the server reads
          // (src/pages/api/forms/[id].ts:170). Read above, where the gate
          // needed it. Absent stays absent for a form without the challenge,
          // so nothing changes for one.
          if (turnstileToken) {
            payloadObj[TURNSTILE_TOKEN_KEY] = turnstileToken;
          }

          // The built-in check's solved answer, under the key the server
          // reads (payload?.altcha, src/pages/api/forms/[id].ts). Absent
          // when the widget's script never loaded ("unavailable"), so the
          // submission goes out with no answer rather than being blocked.
          if (altchaPayloadRef.current) {
            payloadObj.altcha = altchaPayloadRef.current;
          }

          // The fill-time token (task 2.3), under the key the submit route
          // already reads (src/pages/api/forms/[id].ts). Absent when the
          // render-token fetch above never completed, which must never hold
          // up the submission.
          if (fillTimeTokenRef.current) {
            payloadObj[FILL_TIME_TOKEN_FIELD] = fillTimeTokenRef.current;
          }

          // Did the server answer at all? A rejected fetch is an offline
          // visitor or a blocked request, which needs a different sentence
          // from a bug in the code below it.
          let serverAnswered = false;

          /**
           * Give up the token this attempt sent, and ask for another.
           *
           * Called wherever the server has seen the token, whatever it then
           * decided: Cloudflare verifies a token exactly once, so a reply of
           * any kind from below `src/pages/api/forms/[id].ts:173` leaves the
           * widget holding something the next attempt would only replay.
           *
           * Only when a token actually went out. A form with no challenge has
           * no widget of its own, and a reset with no id answers for whichever
           * widget Cloudflare considers first, so an unprotected form would
           * clear the token of a protected one sharing the page.
           *
           * Once per submission, because the outcome branches below can be
           * reached one after another (a success branch that then throws) and
           * a second reset would discard the replacement challenge the first
           * one just asked for.
           *
           * The status goes back to "pending" only when a reset was actually
           * made: the widget is empty until the re-run challenge resolves, so
           * left "solved" the button would invite a click that can only
           * produce an error, and where there is no `reset` to call the old
           * behaviour beats a button that never comes back.
           */
          let tokenRetired = false;
          const retireSubmittedToken = () => {
            if (!turnstileToken || tokenRetired) return;
            tokenRetired = true;
            if (resetTurnstileWidget()) {
              setTurnstileStatus("pending");
            }
          };

          try {
            const response = await fetch(
              `${config.apiUrl}/api/forms/${formData.id}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
                body: JSON.stringify(payloadObj),
              },
            );
            serverAnswered = true;

            // null when the body is not our envelope. Everything below reads
            // it optionally, so a 502's HTML page ends at the generic message
            // instead of at a parser error in the visitor's face.
            const result = await readApiEnvelope(response);

            const msgContainer = form.querySelector(".kiroku-form-messages");
            if (msgContainer) msgContainer.innerHTML = ""; // Clear existing

            if (result?.success) {
              // The token this submission carried is spent. The server verified
              // it, and Cloudflare accepts a token exactly once, so the widget
              // is now holding something a second submission would replay. With
              // `stayOnPage` the visitor is left on a cleared form that invites
              // exactly that, and Cloudflare answers the replay with
              // `timeout-or-duplicate`, which this embed reported as a failed
              // security check to somebody who had done nothing wrong. That is
              // the defect observed on 2026-08-25, 82 seconds after an accepted
              // submission and well inside the five minute token lifetime.
              retireSubmittedToken();

              // What the visitor is shown (plan 6.4). The server recomputed and
              // stored its own tally from the same answers (plan 2.1) rather
              // than trusting anything posted; both sides run this one
              // evaluator, so they agree. `payloadObj` is read rather than
              // `formValues` because the reset below clears the state and a
              // tally taken after it would count an empty form.
              const computedResult = tallyOutcomes(
                quizDefinitionFrom(formData),
                payloadObj,
              );

              if (computedResult) {
                // The result replaces the form (the early return in this
                // component's render), so nothing is written into the message
                // area: a quiz visitor filled this in to be told something,
                // not to be thanked.
                setQuizResult(computedResult);
              } else if (msgContainer) {
                msgContainer.innerHTML =
                  '<div class="kiroku-form-message kiroku-form-success">' +
                  (formData.settings?.successMessage || "Success!") +
                  "</div>";
              }

              if (formData.settings?.stayOnPage !== false) {
                form.reset();
                setFormValues({});
                setCurrentStepIndex(0);
              } else if (formData.redirectUrl) {
                const target = safeNavigationUrl(formData.redirectUrl);
                if (target) {
                  window.location.href = target;
                } else {
                  console.warn(
                    "KirokuEmbed: redirectUrl is not a safe http(s) address; not navigating",
                  );
                }
              }

              // Popup mode: record the conversion (starts the frequency window
              // so it will not reopen), beacon the converted signal with its
              // trigger/frequency, and dismiss the modal.
              if (popupTrigger) {
                popupTrigger.markConverted();
                sendPopupBeacon({
                  event: "converted",
                  trigger: config.triggerOverride ?? popup?.trigger,
                  frequencyDays: popup?.frequencyDays,
                  url: window.location.href,
                  referrer: document.referrer || "",
                  timestamp: new Date().toISOString(),
                });
                // A quiz keeps the modal open: the result is inside it, and
                // dismissing on conversion would throw the answer away the
                // moment it was computed. Every other form shows its success
                // message and closes after POPUP_SUCCESS_VISIBLE_MS. Closing in
                // this tick removed the message the line it was written, so no
                // popup visitor ever saw the owner's thank-you (2026-09-24).
                // The conversion is recorded either way, so the frequency
                // window still starts and the funnel still counts this.
                if (!computedResult) {
                  const submittedForm = form;
                  setTimeout(() => {
                    // Only if this form is still on screen: the visitor may
                    // have closed the modal themselves in the meantime.
                    if (document.contains(submittedForm)) closeModal();
                  }, POPUP_SUCCESS_VISIBLE_MS);
                }
              }
            } else {
              // A rejection spends the token too. The server verifies the
              // challenge at `src/pages/api/forms/[id].ts:173` and validates
              // the payload at :196, in that order, so anything it refuses
              // below that line was refused *after* Cloudflare consumed the
              // token. The visitor is then told to fix a field and retry, and
              // the retry replays a dead token, so a mistyped email comes back
              // as "CAPTCHA verification failed" and there is nothing they can
              // do about it. Same spent token as the accepted case, reached
              // from the other side.
              //
              // The rate limiter at :129 is the exception: it answers at :150,
              // above the verification, so that token never left our server
              // and is still good for the retry the message invites. Keeping
              // it is the standing prohibition holding, since a reset here
              // would discard a live token and can put an interactive
              // challenge in front of somebody whose only mistake was
              // submitting twice quickly.
              //
              // Every other code defaults to a fresh challenge, including any
              // check added later, because the verification sits near the top
              // of that handler and almost everything is below it.
              // `tests/guard/turnstile-reset-matches-server-order.test.ts`
              // fails if that ordering changes.
              if (!isPreVerificationRejection(result)) {
                retireSubmittedToken();
              }

              // Route the server's per-field map to the fields it names. Only
              // fields rendered right now can hold a message, so a key naming
              // another step's field (or the honeypot's `_form`) falls through
              // to the form-level area instead of disappearing.
              const { perField, formLevel } = splitValidationDetails(
                result?.error?.details,
                new Set(visibleFields.map((f: any) => f.name)),
              );
              setFieldErrors(perField);

              if (msgContainer) {
                // Built with textContent rather than innerHTML: these strings
                // come off the wire, and a message is not a place to run
                // markup.
                const lines = [errorMessageFrom(result), ...formLevel];
                for (const line of lines) {
                  const el = document.createElement("div");
                  el.className = "kiroku-form-message kiroku-form-error";
                  el.textContent = line;
                  msgContainer.appendChild(el);
                }
              }
            }
          } catch (error: any) {
            // A request that never arrived leaves the token untouched, so it
            // is still the good one and the retry should carry it: an offline
            // moment must not also cost the visitor a fresh challenge. Once
            // the server has answered, though, it may already have verified,
            // and only the branches above know what it said, so an error
            // thrown after that point gives the token up rather than betting
            // on it.
            if (serverAnswered) retireSubmittedToken();

            // The visitor gets a sentence they can act on; the developer gets
            // the real one in the console. Rendering `error.message` put
            // "Failed to fetch" and "Unexpected token '<'" on customer sites.
            console.error("[Kiroku] Submission failed:", error);
            showFormError(
              form,
              serverAnswered ? GENERIC_SUBMIT_ERROR : UNREACHABLE_SERVER_ERROR,
            );
          } finally {
            setIsSubmitting(false);
          }
        };

        // Optional CTA intro (settings.heading / settings.description) shown
        // once at the top of the form, above the progress bar / step 1 and the
        // fields. Opt-in: blank/absent both -> nothing renders (no spacing
        // change). Passed as createElement text children (never innerHTML), so
        // owner-supplied markup is shown literally, not executed.
        const introHeading =
          typeof formData.settings?.heading === "string"
            ? formData.settings.heading.trim()
            : "";
        const introDescription =
          typeof formData.settings?.description === "string"
            ? formData.settings.description.trim()
            : "";
        const introDescriptionRich: RichTextDocument | null =
          formData.settings?.descriptionRich &&
          Array.isArray(formData.settings.descriptionRich.blocks) &&
          formData.settings.descriptionRich.blocks.length > 0
            ? formData.settings.descriptionRich
            : null;
        const hasIntro = !!introHeading || !!introDescription;

        // A quiz that has produced its result shows the result, in place of the
        // form (plan 6.4). Reached only after a successful submit, and only for
        // a form that declares outcomes. Same position in the render as
        // LiveForm's own early return.
        if (quizResult) {
          const successMessage =
            formData.settings?.successMessage || "Success!";
          // "Not right now" under the CTA. In a popup the modal is the thing
          // being declined, so it closes and the page underneath is back;
          // inline, the result gives way to the ordinary thank-you above the
          // reset form, the state every form without outcomes ends on.
          const dismissOutcome = () => {
            if (popupTrigger) {
              closeModal();
              return;
            }
            dismissedOutcomeMessageRef.current = successMessage;
            setQuizResult(null);
          };
          // Wrapped so the theme's variables are in scope for the result's
          // colours even inline, where there is no card and no
          // `.kiroku-embed-form` to inherit from.
          return React.createElement(
            "div",
            { className: "kiroku-embed-result", style: themeStyle },
            createOutcomeNode(quizResult, successMessage, dismissOutcome),
          );
        }

        return React.createElement(
          "form",
          {
            ref: formRef,
            method: "post",
            className: "kiroku-embed-form",
            style: themeStyle,
            onSubmit: handleSubmit,
            // Capture phase: focus does not bubble, and this reaches the
            // form for a focus on any descendant, including fields
            // conditional logic adds later. Preact supports the "Capture"
            // suffix the same way React does.
            onFocusCapture: trackFormStarted,
          },
          [
            // Intro (heading + description)
            hasIntro &&
              React.createElement(
                "div",
                {
                  key: "intro",
                  className: "kiroku-form-intro",
                  style: { marginBottom: "1.5rem" },
                },
                [
                  introHeading &&
                    React.createElement(
                      "h2",
                      {
                        key: "intro-heading",
                        style: {
                          margin: "0",
                          fontSize: "1.5rem",
                          fontWeight: "700",
                          color: "#1e293b",
                        },
                      },
                      introHeading,
                    ),
                  introDescription &&
                    (introDescriptionRich
                      ? React.createElement(
                          "div",
                          {
                            key: "intro-description",
                            style: {
                              margin: introHeading ? "0.25rem 0 0 0" : "0",
                              color: "#475569",
                            },
                          },
                          richTextBlockNodes(
                            introDescriptionRich,
                            "intro-desc",
                          ),
                        )
                      : React.createElement(
                          "p",
                          {
                            key: "intro-description",
                            style: {
                              margin: introHeading ? "0.25rem 0 0 0" : "0",
                              color: "#475569",
                            },
                          },
                          introDescription,
                        )),
                ],
              ),

            // Progress Bar
            isMultiStep &&
              React.createElement(
                "div",
                { key: "progress", style: { marginBottom: "1.5rem" } },
                [
                  React.createElement(
                    "div",
                    {
                      style: {
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0.5rem",
                        fontSize: "0.875rem",
                        color: "#64748b",
                      },
                    },
                    [
                      React.createElement("span", null, currentStep.title),
                      React.createElement(
                        "span",
                        null,
                        `Step ${currentStepIndex + 1} of ${sortedSteps.length}`,
                      ),
                    ],
                  ),
                  React.createElement(
                    "div",
                    {
                      style: {
                        width: "100%",
                        height: "0.5rem",
                        backgroundColor: "#e2e8f0",
                        borderRadius: "9999px",
                        overflow: "hidden",
                      },
                    },
                    [
                      React.createElement("div", {
                        style: {
                          width: `${((currentStepIndex + 1) / sortedSteps.length) * 100}%`,
                          height: "100%",
                          backgroundColor:
                            "var(--kiroku-color-primary, #3b82f6)",
                          transition: "width 0.3s ease",
                        },
                      }),
                    ],
                  ),
                ],
              ),

            // Messages
            React.createElement("div", {
              key: "messages",
              className: "kiroku-form-messages",
              "aria-live": "polite",
            }),

            // Plan form-design-gaps 1.1: explains the `*` drawn beside a
            // required field's label (requiredStar above), which nothing in
            // this renderer otherwise accounts for. One per step, so a
            // multi-step form only shows it on steps that have a required
            // field (visibleFields is already filtered to the current step).
            // Content fields and the honeypot never draw the star even when
            // required (the `!isContentField` guard around requiredStar
            // below), so they are excluded here too.
            visibleFields.some(
              (field: any) =>
                field.required &&
                field.type !== "static-text" &&
                field.type !== "static-image" &&
                field.type !== "download-link" &&
                field.type !== "honeypot",
            ) &&
              React.createElement(
                "p",
                {
                  key: "required-note",
                  className: "kiroku-required-note",
                  style: {
                    fontSize: "0.875rem",
                    color: "#64748b",
                    marginBottom: "0.5rem",
                  },
                },
                "* Required",
              ),

            // Fields
            ...visibleFields.map((field: any) => {
              const options = parseOptions(field.options);
              // A checkbox with options is a group of inputs, so unlike a lone
              // boolean checkbox it needs the field label shown as the group's
              // question. Without it the user sees three tick boxes and no
              // indication of what is being asked.
              const isCheckboxGroup =
                field.type === "checkbox" && options.length > 0;
              // Field types that display something and collect nothing. They
              // render no control at all, so their group label cannot be a
              // <label>: its `for` would point at an input that does not exist.
              const isContentField =
                field.type === "static-text" ||
                field.type === "static-image" ||
                field.type === "download-link";
              // A scale is a radio group, so like a checkbox group its question
              // labels the group rather than any one input: a `<label for>`
              // would have to name one of eleven radios.
              const isRatingGroup =
                field.type === "rating" || field.type === "nps";
              // A trap for bots, so it must be invisible to people.
              const isHoneypot = field.type === "honeypot";
              const groupLabelId = `${field.name}-label`;
              // The server's message for this field on the last attempt, if
              // any. A honeypot rejection is never shown beside its trap.
              const fieldError = isHoneypot
                ? undefined
                : fieldErrors[field.name];
              const fieldErrorId = `${field.name}-error`;
              // Marks the control as rejected and points assistive tech at the
              // message rendered below it. Spread onto every branch that builds
              // an input by hand as well as onto commonProps: a checkbox group
              // is as rejectable as a text box. Absent entirely when there is
              // no error, so nothing carries a stale aria-invalid.
              const errorProps: Record<string, string> = fieldError
                ? { "aria-invalid": "true", "aria-describedby": fieldErrorId }
                : {};

              // Common props for all inputs
              const commonProps = {
                key: "input",
                id: field.name,
                name: field.name,
                required: field.required || false,
                className: field.className || "kiroku-input", // Ensure you have basic styling
                onChange: handleChange,
                value: formValues[field.name] || "",
                ...errorProps,
              };

              let inputElement;

              switch (field.type) {
                case "textarea":
                  inputElement = React.createElement("textarea", {
                    ...commonProps,
                    placeholder: field.placeholder || "",
                    rows: 4,
                  });
                  break;

                case "select": {
                  const selectEl = React.createElement("select", commonProps, [
                    React.createElement(
                      "option",
                      { key: "default", value: "" },
                      field.placeholder || "Select an option",
                    ),
                    ...options.map((opt) =>
                      React.createElement(
                        "option",
                        { key: opt.value, value: opt.value },
                        opt.label,
                      ),
                    ),
                    field.allowOther &&
                      React.createElement(
                        "option",
                        { key: "other", value: OTHER_OPTION_VALUE },
                        "Other",
                      ),
                  ]);
                  if (!field.allowOther) {
                    inputElement = selectEl;
                    break;
                  }
                  // allowOther: formValues[field.name] carries the sentinel
                  // until something is typed, exactly as LiveForm.tsx does --
                  // this renderer's submission is built from that object
                  // directly (payloadObj = { ...formValues }), not from a
                  // native <form>'s `name` attributes, so there is no second
                  // key to avoid and no renaming trick to play.
                  const selectValueStr = String(formValues[field.name] ?? "");
                  const selectOptionValues = options.map((o) =>
                    String(o.value),
                  );
                  const selectOtherActive = isOtherValue(
                    selectValueStr,
                    selectOptionValues,
                  );
                  inputElement = [
                    selectEl,
                    selectOtherActive &&
                      React.createElement("input", {
                        key: "other-text",
                        type: "text",
                        id: `${field.name}-other`,
                        value:
                          selectValueStr === OTHER_OPTION_VALUE
                            ? ""
                            : selectValueStr,
                        placeholder: "Please specify",
                        "aria-label": `${field.label || "Other"}: please specify`,
                        className: field.className || "kiroku-input",
                        style: { marginTop: "0.5rem" },
                        onChange: (e: any) => {
                          const typed = e.target.value;
                          setFormValues((prev: any) => ({
                            ...prev,
                            [field.name]:
                              typed === "" ? OTHER_OPTION_VALUE : typed,
                          }));
                        },
                      }),
                  ];
                  break;
                }

                case "radio": {
                  const radioValueStr = String(formValues[field.name] ?? "");
                  const radioOptionValues = options.map((o) => String(o.value));
                  const radioOtherActive = isOtherValue(
                    radioValueStr,
                    radioOptionValues,
                  );
                  const radioGroupEl = React.createElement(
                    "div",
                    { key: "input", className: "kiroku-radio-group" },
                    [
                      ...options.map((opt) =>
                        React.createElement(
                          "div",
                          {
                            key: opt.value,
                            style: {
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "0.5rem",
                            },
                          },
                          [
                            React.createElement("input", {
                              key: "input",
                              type: "radio",
                              id: `${field.name}-${opt.value}`,
                              name: field.name,
                              // Option values are typed `string | number |
                              // boolean` and the DOM only ever hands back a
                              // string, so a numeric option never matched and so
                              // never showed as selected. Same coercion the
                              // checkbox group needed.
                              value: String(opt.value),
                              checked:
                                String(formValues[field.name] ?? "") ===
                                String(opt.value),
                              // `required` on any radio in a group means one of
                              // the group must be chosen, so it needs no
                              // emptiness dance of the kind checkboxes do.
                              required: !!field.required,
                              onChange: handleChange,
                              style: { marginRight: "0.5rem" },
                              ...errorProps,
                            }),
                            React.createElement(
                              "label",
                              {
                                key: "label",
                                htmlFor: `${field.name}-${opt.value}`,
                              },
                              opt.label,
                            ),
                          ],
                        ),
                      ),
                      field.allowOther &&
                        React.createElement(
                          "div",
                          {
                            key: "other",
                            style: {
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "0.5rem",
                            },
                          },
                          [
                            React.createElement("input", {
                              key: "input",
                              type: "radio",
                              id: `${field.name}-other`,
                              name: field.name,
                              value: OTHER_OPTION_VALUE,
                              checked: radioOtherActive,
                              required: !!field.required,
                              onChange: handleChange,
                              style: { marginRight: "0.5rem" },
                              ...errorProps,
                            }),
                            React.createElement(
                              "label",
                              { key: "label", htmlFor: `${field.name}-other` },
                              "Other",
                            ),
                          ],
                        ),
                    ],
                  );
                  if (!field.allowOther) {
                    inputElement = radioGroupEl;
                    break;
                  }
                  inputElement = [
                    radioGroupEl,
                    radioOtherActive &&
                      React.createElement("input", {
                        key: "other-text",
                        type: "text",
                        id: `${field.name}-other-text`,
                        value:
                          radioValueStr === OTHER_OPTION_VALUE
                            ? ""
                            : radioValueStr,
                        placeholder: "Please specify",
                        "aria-label": `${field.label || "Other"}: please specify`,
                        className: field.className || "kiroku-input",
                        style: { marginTop: "0.5rem" },
                        onChange: (e: any) => {
                          const typed = e.target.value;
                          setFormValues((prev: any) => ({
                            ...prev,
                            [field.name]:
                              typed === "" ? OTHER_OPTION_VALUE : typed,
                          }));
                        },
                      }),
                  ];
                  break;
                }

                case "checkbox":
                  // A field carrying options renders one tickable input per
                  // option and stores an array; handleChange keys off the
                  // data-kiroku-multi marker below. A field with no options is
                  // a single boolean, which is the consent tick box shape.
                  if (options.length > 0) {
                    const selected = Array.isArray(formValues[field.name])
                      ? formValues[field.name]
                      : [];
                    // Native `required` on a checkbox means *that* box must be
                    // ticked, unlike a radio group where it means "one of
                    // these". So the attribute goes on every option while the
                    // group is empty and comes off all of them the moment one
                    // is ticked: that is what "at least one" costs in native
                    // constraint validation. aria-required on the group carries
                    // the real semantic, which is about the group, not the box.
                    const groupNeedsAnswer =
                      !!field.required && selected.length === 0;
                    const checkboxOptionValues = options.map((o) =>
                      String(o.value),
                    );
                    // allowOther: at most one array entry is ever "the Other
                    // slot" -- the sentinel (checked, nothing typed) or the
                    // typed text (matches no listed option). Toggling the
                    // Other checkbox itself reuses the generic `handleChange`
                    // above (it just adds/removes a literal value like any
                    // other option); only the paired text box needs this
                    // find-and-replace, mirroring LiveForm.tsx exactly.
                    const otherEntry = field.allowOther
                      ? selected.find((v: any) =>
                          isOtherValue(v, checkboxOptionValues),
                        )
                      : undefined;
                    const otherChecked = otherEntry !== undefined;
                    const otherText =
                      otherEntry === undefined ||
                      otherEntry === OTHER_OPTION_VALUE
                        ? ""
                        : otherEntry;
                    // Task 1.2, docs/consent-link-and-retention-notice_plan.md.
                    const consentPrivacyLinkUrl = resolveConsentPrivacyLink(
                      field,
                      formData.settings?.privacyPolicyUrl,
                    );
                    const checkboxGroupEl = React.createElement(
                      "div",
                      {
                        key: "input",
                        className: "kiroku-checkbox-group",
                        role: "group",
                        "aria-labelledby": groupLabelId,
                        "aria-required": field.required ? "true" : undefined,
                      },
                      [
                        ...options.map((opt, optIndex) => {
                          // Option values are typed `string | number | boolean`,
                          // and the DOM only ever hands back a string, so compare
                          // and store the coerced form or nothing matches.
                          const optValue = String(opt.value);
                          // Index in the id, not the value: an option value may
                          // contain characters that are illegal in an id, and two
                          // fields on one page may share an option value.
                          const optId = `${field.name}-opt-${optIndex}`;
                          return React.createElement(
                            "div",
                            {
                              key: optId,
                              style: {
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "0.5rem",
                              },
                            },
                            [
                              React.createElement("input", {
                                key: "input",
                                type: "checkbox",
                                id: optId,
                                name: field.name,
                                value: optValue,
                                "data-kiroku-multi": "true",
                                required: groupNeedsAnswer,
                                checked: selected.indexOf(optValue) !== -1,
                                onChange: handleChange,
                                style: { marginRight: "0.5rem" },
                                ...errorProps,
                              }),
                              React.createElement(
                                "label",
                                { key: "label", htmlFor: optId },
                                opt.richLabel && opt.richLabel.blocks.length > 0
                                  ? richTextInlineOnlyNodes(
                                      opt.richLabel,
                                      `option-${optId}`,
                                    )
                                  : opt.label,
                              ),
                              // A sibling of the label, never nested inside
                              // it: clicking it must not also toggle the
                              // checkbox via the label's own click-forwarding.
                              consentPrivacyLinkUrl &&
                                React.createElement(
                                  "a",
                                  {
                                    key: "privacy-link",
                                    href: consentPrivacyLinkUrl,
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                    style: { marginLeft: "0.25rem" },
                                  },
                                  CONSENT_PRIVACY_LINK_TEXT,
                                ),
                            ],
                          );
                        }),
                        field.allowOther &&
                          React.createElement(
                            "div",
                            {
                              key: "other",
                              style: {
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "0.5rem",
                              },
                            },
                            [
                              React.createElement("input", {
                                key: "input",
                                type: "checkbox",
                                id: `${field.name}-other`,
                                name: field.name,
                                value: OTHER_OPTION_VALUE,
                                checked: otherChecked,
                                // Not the generic handleChange: once text has
                                // replaced the sentinel in the array, its own
                                // DOM `value` is still the literal
                                // "__other__", so unticking by filtering on
                                // that exact string would leave the typed
                                // answer stranded. isOtherValue finds the
                                // Other slot whatever it currently holds.
                                onChange: (e: any) => {
                                  const checked = e.target.checked;
                                  setFormValues((prev: any) => {
                                    const current = Array.isArray(
                                      prev[field.name],
                                    )
                                      ? prev[field.name]
                                      : [];
                                    const next = current.filter(
                                      (v: any) =>
                                        !isOtherValue(v, checkboxOptionValues),
                                    );
                                    if (checked) next.push(OTHER_OPTION_VALUE);
                                    return { ...prev, [field.name]: next };
                                  });
                                },
                                style: { marginRight: "0.5rem" },
                                ...errorProps,
                              }),
                              React.createElement(
                                "label",
                                {
                                  key: "label",
                                  htmlFor: `${field.name}-other`,
                                },
                                "Other",
                              ),
                            ],
                          ),
                      ],
                    );
                    if (!field.allowOther) {
                      inputElement = checkboxGroupEl;
                    } else {
                      inputElement = [
                        checkboxGroupEl,
                        otherChecked &&
                          React.createElement("input", {
                            key: "other-text",
                            type: "text",
                            id: `${field.name}-other-text`,
                            value: otherText,
                            placeholder: "Please specify",
                            "aria-label": `${field.label || "Other"}: please specify`,
                            className: field.className || "kiroku-input",
                            style: { marginTop: "0.5rem" },
                            onChange: (e: any) => {
                              const typed = e.target.value;
                              setFormValues((prev: any) => {
                                const current = Array.isArray(prev[field.name])
                                  ? prev[field.name]
                                  : [];
                                const next = current.filter(
                                  (v: any) =>
                                    !isOtherValue(v, checkboxOptionValues),
                                );
                                next.push(
                                  typed === "" ? OTHER_OPTION_VALUE : typed,
                                );
                                return { ...prev, [field.name]: next };
                              });
                            },
                          }),
                      ];
                    }
                  } else {
                    // Single boolean checkbox
                    const consentPrivacyLinkUrl = resolveConsentPrivacyLink(
                      field,
                      formData.settings?.privacyPolicyUrl,
                    );
                    inputElement = React.createElement(
                      "div",
                      {
                        key: "input",
                        style: { display: "flex", alignItems: "center" },
                      },
                      [
                        React.createElement("input", {
                          key: "input",
                          type: "checkbox",
                          id: field.name,
                          name: field.name,
                          required: !!field.required,
                          checked: !!formValues[field.name],
                          onChange: handleChange,
                          style: { marginRight: "0.5rem" },
                          ...errorProps,
                        }),
                        React.createElement(
                          "label",
                          { key: "label", htmlFor: field.name },
                          [
                            field.placeholder || field.label,
                            // The field-group label is suppressed for a lone
                            // boolean checkbox, so this is the only place its
                            // required marker can go.
                            field.required && requiredStar("checkbox-required"),
                          ],
                        ),
                        // A sibling of the label, never nested inside it.
                        consentPrivacyLinkUrl &&
                          React.createElement(
                            "a",
                            {
                              key: "privacy-link",
                              href: consentPrivacyLinkUrl,
                              target: "_blank",
                              rel: "noopener noreferrer",
                              style: { marginLeft: "0.25rem" },
                            },
                            CONSENT_PRIVACY_LINK_TEXT,
                          ),
                      ],
                    );
                  }
                  break;

                // The four types below collect nothing. Until now they fell
                // through to `default:` and became <input type="static-text">,
                // <input type="honeypot"> and so on, which every browser
                // renders as a plain text box: a labelled field asking the
                // visitor to fill in something the server never reads, and a
                // required one blocked submission outright. The honeypot was
                // the worst of them, because a visible trap catches people, and
                // filling it is exactly what validation.ts:52 rejects as a bot.

                case "static-text":
                  inputElement =
                    field.richText && field.richText.blocks.length > 0
                      ? React.createElement(
                          "div",
                          {
                            key: "input",
                            className: "kiroku-static-text",
                          },
                          richTextBlockNodes(field.richText, "static-text"),
                        )
                      : React.createElement(
                          "div",
                          {
                            key: "input",
                            className: "kiroku-static-text",
                            style: { whiteSpace: "pre-wrap" },
                          },
                          String(field.defaultValue ?? ""),
                        );
                  break;

                case "static-image":
                  // No URL means nothing to show: a bare <img> with an empty
                  // src draws a broken-image icon on the customer's page.
                  inputElement = field.defaultValue
                    ? React.createElement(
                        "figure",
                        {
                          key: "input",
                          className: "kiroku-static-image",
                          style: {
                            margin: "0",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          },
                        },
                        [
                          React.createElement("img", {
                            key: "img",
                            src: String(field.defaultValue),
                            alt: field.placeholder || "",
                            style: {
                              maxWidth: "100%",
                              height: "auto",
                              maxHeight: "400px",
                            },
                          }),
                          field.helpText &&
                            React.createElement(
                              "figcaption",
                              {
                                key: "caption",
                                style: {
                                  marginTop: "0.5rem",
                                  fontSize: "0.875rem",
                                  color: "#6b7280",
                                },
                              },
                              field.helpText,
                            ),
                        ],
                      )
                    : null;
                  break;

                case "download-link":
                  inputElement = field.defaultValue
                    ? React.createElement(
                        "a",
                        {
                          key: "input",
                          className: "kiroku-download-link",
                          href: String(field.defaultValue),
                          // Empty `download` asks for the server's own filename.
                          // It is a no-op cross-origin, which files on our blob
                          // store always are, so the tab is the fallback.
                          download: "",
                          target: "_blank",
                          rel: "noopener noreferrer",
                          style: {
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            padding: "0.5rem 1rem",
                            borderRadius:
                              "var(--kiroku-border-radius, 0.25rem)",
                            background: "var(--kiroku-color-primary, #3b82f6)",
                            color: "#fff",
                            fontWeight: "500",
                            textDecoration: "none",
                          },
                        },
                        field.placeholder || field.label || "Download",
                      )
                    : null;
                  break;

                /*
                 * A scale, drawn as a real radio group. `rating` is the
                 * configurable 3-10 points, `nps` the fixed 0-10 with its
                 * anchors, and `ratingScale()` resolves which before either
                 * gets here, so this is one code path for two types.
                 *
                 * Framework-free in the sense docs/architecture.md means: this
                 * builds the markup itself rather than importing
                 * RatingInput.tsx, because that component is React and this
                 * bundle is Preact on somebody else's page. What it shares with
                 * the other three renderers is the `kiroku-rating-*` class
                 * contract and `ratingPoints()`, so a scale one of them refuses
                 * to draw is one all four refuse to draw.
                 *
                 * The radios are visually hidden by the stylesheet rather than
                 * removed, so they stay focusable and a screen reader still
                 * announces "3, radio, 3 of 5" while the visible box is the
                 * <label>.
                 */
                case "rating":
                case "nps": {
                  const scale = ratingScale(field.type, field.validation);
                  const points = ratingPoints(scale, field.name);

                  // An unusable scale draws nothing rather than a million
                  // radios: `max` comes out of stored JSON and a broken import
                  // can put anything in it. `ratingPoints` has already warned
                  // naming the field.
                  if (!points) {
                    inputElement = null;
                    break;
                  }

                  const current = String(formValues[field.name] ?? "");
                  const selectedIndex = points.findIndex(
                    (point) => String(point) === current,
                  );

                  /*
                   * The ends clamp, they do not wrap, matching RatingInput on
                   * the hosted page. A native radio group cycles from the last
                   * option back to the first, which on a scale turns one arrow
                   * press too many into the opposite answer: on NPS, a promoter
                   * recorded as a detractor. `preventDefault` stops the browser
                   * also moving, or the selection would jump two per press.
                   */
                  const handleRatingKeyDown = (event: any) => {
                    let nextIndex: number;
                    switch (event.key) {
                      case "ArrowRight":
                      case "ArrowDown":
                        nextIndex =
                          selectedIndex < 0
                            ? 0
                            : Math.min(selectedIndex + 1, points.length - 1);
                        break;
                      case "ArrowLeft":
                      case "ArrowUp":
                        nextIndex =
                          selectedIndex < 0
                            ? points.length - 1
                            : Math.max(selectedIndex - 1, 0);
                        break;
                      case "Home":
                        nextIndex = 0;
                        break;
                      case "End":
                        nextIndex = points.length - 1;
                        break;
                      default:
                        // Space and Tab stay the browser's: space already
                        // selects the focused radio and Tab must leave.
                        return;
                    }

                    event.preventDefault();
                    if (nextIndex === selectedIndex) return;

                    const nextValue = String(points[nextIndex]);
                    setFormValues((prev: any) => ({
                      ...prev,
                      [field.name]: nextValue,
                    }));
                    // Focus follows selection, so the next press continues from
                    // here rather than from wherever the tab ring left off.
                    const group = event.currentTarget;
                    const next =
                      group &&
                      group.querySelector('input[value="' + nextValue + '"]');
                    if (next && next.focus) next.focus();
                  };

                  inputElement = React.createElement(
                    "div",
                    {
                      key: "input",
                      className: "kiroku-rating kiroku-rating-points",
                      role: "radiogroup",
                      "aria-labelledby": groupLabelId,
                      "aria-required": field.required ? "true" : undefined,
                      onKeyDown: handleRatingKeyDown,
                      ...errorProps,
                    },
                    points.map((point, pointIndex) => {
                      const pointId = `${field.name}-rating-${pointIndex}`;
                      /*
                       * The anchor word joins the accessible name of the end it
                       * anchors, so somebody arrowing onto one hears what it
                       * means rather than a bare number. The visible digit
                       * still starts the name, which is what WCAG 2.5.3 asks.
                       */
                      const anchor =
                        pointIndex === 0
                          ? scale.minLabel
                          : pointIndex === points.length - 1
                            ? scale.maxLabel
                            : undefined;

                      return React.createElement(
                        "span",
                        { key: pointId, className: "kiroku-rating-point" },
                        [
                          React.createElement("input", {
                            key: "input",
                            type: "radio",
                            id: pointId,
                            name: field.name,
                            value: String(point),
                            checked: String(point) === current,
                            // `required` on any radio in a group means "one of
                            // these", so it needs no emptiness dance of the
                            // kind checkboxes do.
                            required: !!field.required,
                            "aria-label": anchor
                              ? `${point} ${anchor}`
                              : undefined,
                            onChange: handleChange,
                          }),
                          React.createElement(
                            "label",
                            { key: "label", htmlFor: pointId },
                            String(point),
                          ),
                        ],
                      );
                    }),
                  );

                  if (scale.minLabel || scale.maxLabel) {
                    // The words under either end, for everyone who is looking
                    // rather than listening. aria-hidden because the same two
                    // words are already in the end points' accessible names,
                    // and hearing them twice is worse than not at all.
                    inputElement = React.createElement(
                      "div",
                      { key: "input" },
                      [
                        React.createElement(
                          "div",
                          { key: "scale" },
                          inputElement,
                        ),
                        React.createElement(
                          "div",
                          {
                            key: "anchors",
                            className: "kiroku-rating-anchors",
                            "aria-hidden": "true",
                          },
                          [
                            React.createElement(
                              "span",
                              { key: "min" },
                              scale.minLabel || "",
                            ),
                            React.createElement(
                              "span",
                              { key: "max" },
                              scale.maxLabel || "",
                            ),
                          ],
                        ),
                      ],
                    );
                  }
                  break;
                }

                case "honeypot":
                  // Out of the tab order, and with no label: text inside a
                  // display:none subtree is still in textContent, and a screen
                  // reader skips the subtree entirely, so a label here would be
                  // read by nothing but the bot it is baiting. The `name` is
                  // the bait, and the group below carries the display:none.
                  inputElement = React.createElement("input", {
                    key: "input",
                    type: "text",
                    id: field.name,
                    name: field.name,
                    autoComplete: "off",
                    tabIndex: -1,
                    value: formValues[field.name] || "",
                    onChange: handleChange,
                    // A person cannot focus this input, so a focus on it came
                    // from software. isAnsweringControl reads this to keep
                    // bot traffic out of the form_started count, matching
                    // LiveForm.tsx and formEmbedUtils.ts's html-mode markup.
                    "data-kiroku-honeypot": "true",
                  });
                  break;

                default:
                  // Text, email, number, etc.
                  inputElement = React.createElement("input", {
                    ...commonProps,
                    type: field.type,
                    placeholder: field.placeholder || "",
                    // Same derived token as LiveForm and the HTML snippet, so
                    // a browser offers a saved email or name in a popup too.
                    autoComplete:
                      autocompleteToken(field.type, field.name, field.label) ??
                      undefined,
                  });
              }

              return React.createElement(
                "div",
                {
                  key: field.id,
                  // The honeypot is hidden by its own inline style rather than
                  // by a class: the embed renders onto somebody else's page,
                  // where our stylesheet may be blocked, overridden or never
                  // loaded, and a honeypot that shows up is a trap for humans.
                  style: isHoneypot
                    ? { display: "none" }
                    : { marginBottom: "1rem" },
                  // task 4.3 (docs/spam-defence_plan.md): display:none already
                  // keeps a screen reader from announcing this, but the plan
                  // asks for it explicitly rather than relying on that alone.
                  ...(isHoneypot ? { "aria-hidden": "true" } : {}),
                  className: `kiroku-field-group kiroku-field-${field.type}`,
                },
                [
                  !isHoneypot &&
                    (field.type !== "checkbox" || isCheckboxGroup) &&
                    React.createElement(
                      // Don't show standard label for single checkbox; its own
                      // label sits beside the input. A checkbox group, a rating
                      // scale, and any content field are labelled by a plain
                      // element because `for` needs one input to point at.
                      isCheckboxGroup || isContentField || isRatingGroup
                        ? "div"
                        : "label",
                      {
                        key: "label",
                        ...(isCheckboxGroup || isRatingGroup
                          ? { id: groupLabelId }
                          : isContentField
                            ? {}
                            : { htmlFor: field.name }),
                        style: {
                          display: "block",
                          marginBottom: "0.25rem",
                          fontWeight: "500",
                        },
                      },
                      [
                        field.label,
                        // A content field cannot be filled in, so a required
                        // marker beside one asks for something impossible.
                        !isContentField &&
                          field.required &&
                          requiredStar("required"),
                      ],
                    ),
                  inputElement,
                  field.helpText &&
                    React.createElement(
                      "small",
                      {
                        key: "help",
                        className: "kiroku-form-helptext",
                        style: {
                          display: "block",
                          marginTop: "0.25rem",
                          color: "#6b7280",
                          fontSize: "0.875rem",
                        },
                      },
                      field.helpText,
                    ),
                  // The server's message for this field, from the 422 body.
                  // Passed as a text child, so owner- and server-supplied text
                  // is shown literally and never parsed as markup. Sits below
                  // the help text because it is the newer, more urgent of the
                  // two.
                  fieldError &&
                    React.createElement(
                      "p",
                      {
                        key: "error",
                        id: fieldErrorId,
                        className: "kiroku-field-error",
                        style: {
                          display: "block",
                          margin: "0.25rem 0 0 0",
                          color: "#dc2626",
                          fontSize: "0.875rem",
                        },
                      },
                      fieldError,
                    ),
                ],
              );
            }),

            // The anti-bot challenge, between the last field and the submit
            // button: where the visitor is already looking when they finish,
            // and inside the <form>, which is where Cloudflare puts its own
            // hidden response input. Only on the step that submits, and only
            // when a site key came with the config.
            turnstileSiteKey &&
              isLastStep &&
              React.createElement("div", {
                key: "turnstile",
                ref: turnstileRef,
                // `cf-turnstile` for parity with HTML mode's markup
                // (generateTurnstileHtml). The widget is drawn explicitly, so
                // the class is a handle rather than the trigger.
                className: "cf-turnstile kiroku-turnstile",
                "data-sitekey": turnstileSiteKey,
                style: { marginTop: "1rem" },
              }),

            // The built-in visitor check's widget (task 2.2), the self-hosted
            // alternative to Turnstile above. `auto="onload"` starts solving
            // the moment the widget's script defines the element -- no click
            // needed (task 0.2's "no setup"). `challenge` is the URL task
            // 1.2 built; the widget fetches it itself.
            visitorCheck === "builtin" &&
              isLastStep &&
              React.createElement("altcha-widget", {
                key: "altcha",
                ref: altchaRef,
                challenge: `${config.apiUrl}/api/forms/${formData.id}/altcha`,
                auto: "onload",
                name: "altcha",
                style: { marginTop: "1rem" },
              }),

            // Why the submit button is disabled, when the reason is one the
            // visitor can act on. role="alert" rather than the form's polite
            // live region: this appears after they have finished filling the
            // form in, next to a button that has just stopped working. That
            // holds for a renewal too, so the role does not change with the
            // status: swapping a live region's role on an element already in
            // the DOM is not reliably re-announced, and a button that stopped
            // working is worth interrupting for whether or not it is coming
            // back.
            turnstileSiteKey &&
              isLastStep &&
              turnstileMessage &&
              React.createElement(
                "p",
                {
                  key: "turnstile-status",
                  className: "kiroku-turnstile-status",
                  role: "alert",
                  style: {
                    margin: "0.5rem 0 0 0",
                    color: turnstileMessageIsFailure ? "#dc2626" : "#475569",
                    fontSize: "0.875rem",
                  },
                },
                turnstileMessage,
              ),

            // Actions
            React.createElement(
              "div",
              {
                key: "actions",
                style: {
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "1.5rem",
                },
              },
              [
                isMultiStep && !isFirstStep
                  ? React.createElement(
                      "button",
                      {
                        type: "button",
                        onClick: handleBack,
                        className: "kiroku-back-button",
                        style: {
                          marginRight: "auto",
                          padding: "0.5rem 1rem",
                          border: "1px solid #e2e8f0",
                          background: "transparent",
                          borderRadius: "0.25rem",
                        },
                      },
                      "Back",
                    )
                  : React.createElement("div"), // Spacer

                !isLastStep
                  ? React.createElement(
                      "button",
                      {
                        type: "button",
                        onClick: handleNext,
                        className: "kiroku-next-button",
                        style: {
                          padding: "0.5rem 1rem",
                          background: "var(--kiroku-color-primary, #3b82f6)",
                          color: "white",
                          borderRadius: "0.25rem",
                          border: "none",
                        },
                      },
                      "Next",
                    )
                  : React.createElement(
                      "button",
                      {
                        type: "submit",
                        className: "kiroku-submit-button",
                        disabled:
                          isSubmitting || turnstileBlocked || altchaBlocked,
                        // A disabled control explains itself or it reads as a
                        // broken form. The status line above carries the two
                        // Turnstile failures; this covers the ordinary wait,
                        // where there is no message on screen yet, and the
                        // built-in check, which never shows a status line at
                        // all (task 2.2: it recovers silently).
                        title: turnstileBlocked
                          ? turnstileMessage || TURNSTILE_PENDING_ERROR
                          : altchaBlocked
                            ? ALTCHA_WAIT_MESSAGE
                            : undefined,
                      },
                      isSubmitting
                        ? "Sending..."
                        : formData.settings?.submitButtonText || "Submit",
                    ),
              ],
            ),

            // Decline control (single-step layout): an explicit way out
            // under the submit button, carrying the same weight as it
            // (DECLINE_BUTTON_STYLE). Only in popup mode with decline enabled;
            // the submit button stays the accept action.
            popupEnabled &&
              popup?.decline?.enabled &&
              React.createElement(
                "div",
                {
                  key: "decline",
                  style: DECLINE_ROW_STYLE,
                },
                React.createElement(
                  "button",
                  {
                    type: "button",
                    className: "kiroku-popup-decline",
                    onClick: handleDecline,
                    style: DECLINE_BUTTON_STYLE,
                  },
                  popup?.decline?.text || DEFAULT_DECLINE_TEXT,
                ),
              ),

            // Branding, gated on the decision the embed config carries.
            // GET /api/forms/<id>/embed?mode=json derives `showBranding` from
            // the owner's subscription and returns the boolean, never the plan
            // name (the endpoint is public and answers any origin). PRO and
            // BUSINESS pay for white-label, so this node is the difference
            // between honouring that and not.
            //
            // Only an explicit `false` hides it: an older cached response with
            // no key at all, or a lookup that failed server-side, must show the
            // attribution rather than give the paid feature away to a form we
            // cannot prove is paid for.
            showBranding &&
              React.createElement(
                "div",
                {
                  key: "branding",
                  className: "kiroku-attribution",
                },
                React.createElement(
                  "a",
                  {
                    // Tagged with the site this embed runs on, so a click
                    // reports which customer's page sent the visitor.
                    href: attributionHref(
                      config.apiUrl,
                      "embed",
                      window.location.hostname,
                    ),
                    target: "_blank",
                    rel: "noopener",
                  },
                  "Powered by KirokuForms",
                ),
              ),
          ],
        );
      };

      // In popup mode the form lives inside a modal, not inline: render into a
      // detached host and clear the inline loading state. Otherwise render into
      // the page container exactly as before.
      const renderTarget: HTMLElement = popupEnabled
        ? document.createElement("div")
        : container;
      if (popupEnabled) {
        renderTarget.className = "kiroku-popup-form-host";
        container.innerHTML = "";
      }

      // Render to the resolved target
      if (ReactDOM.createRoot) {
        // React 18+
        const root = ReactDOM.createRoot(renderTarget);
        root.render(React.createElement(KirokuFormComponent));
      } else {
        // React 17 fallback
        ReactDOM.render(React.createElement(KirokuFormComponent), renderTarget);
      }

      // Open the modal on the trigger (settings/placement path) or immediately
      // (programmatic open()). Both record the show; the frequency cap only
      // gates the armed path, since an explicit open() is host-decided.
      if (popupEnabled && popupTrigger) {
        /*
         * Runs once per open, not once per page: since plan task 4.1 a
         * click-triggered popup reopens on every press, so this whole body
         * repeats. Both counters repeat with it, deliberately (task 4.2 of
         * docs/conversational-forms_plan.md).
         *
         * `popup_shown` is emitted again on every reopen, because each one is a
         * real view: the visitor saw the form. Dropping the later ones would
         * put the funnel's converted/shown ratio above 1 as soon as somebody
         * converts on their third press, and would hide that a button people
         * reopen is doing its job.
         *
         * Three deliberate opens are still not three impressions of an
         * uninvited popup, and nothing reads them as one: the beacon carries
         * `trigger`, and getPopupFunnel (src/lib/analytics/tracker.ts) buckets
         * one row per trigger, so a `click` row never merges into an `exit`
         * row. `markShown()` counts on the same terms; see its note in
         * src/scripts/popupTriggers.ts.
         *
         * Pinned by tests/popup-reopen-emits-a-view.test.tsx against this built
         * artifact, and tests/analytics-popup-funnel.test.ts for the reading.
         */
        // `userInitiated` is true for a click-triggered open and for the
        // programmatic open() API (`openNow`, below): the visitor already
        // asked, so the offer screen never gates those (plan task 3.2,
        // "Out of scope"). `popupTrigger.arm` passes it through from the
        // descriptor that actually fired, since `config.triggerOverride` can
        // be a compound string (`"scroll:50, click:#cta"`) a plain equality
        // check here could not read correctly.
        //
        // `reason` (task 4.1 of docs/popup-triggers-and-funnel_plan.md) is the
        // same thing one level more specific: which signal actually fired, not
        // only which trigger type was configured (`mouse_top`/`blur`/
        // `visibility`/`flick`/`keyboard` for `exit`, the trigger type itself
        // otherwise). `popupTrigger.arm` supplies it for every engine-armed
        // open; `openNow`'s direct `showModal(true)` call below supplies none,
        // so it falls back to `"click"`, the reason closest to what a
        // programmatic open already is -- a visitor-initiated ask, the same
        // thing the `click` trigger reports.
        const showModal = (userInitiated = false, reason = "click") => {
          popupTrigger.markShown();
          const trigger = config.triggerOverride ?? popup?.trigger;
          // Task 4.2 of docs/popup-triggers-and-funnel_plan.md: `msOpen` on the
          // eventual `dismissed` beacon is measured from here, when the
          // visitor actually saw the popup, not from when the trigger fired.
          const shownAt = Date.now();
          sendPopupBeacon({
            event: "shown",
            trigger,
            reason,
            frequencyDays: popup?.frequencyDays,
            url: window.location.href,
            referrer: document.referrer || "",
            timestamp: new Date().toISOString(),
          });
          const offerScreen = popup?.offerScreen;
          openModal(renderTarget, {
            // The card's shape and, for slide-in/bar, which edge or corner it
            // anchors to (docs/popup-formats-and-targeting_plan.md task 2.5).
            // Absent falls back to openModal's own "modal" default, so a form
            // saved before this field existed renders exactly as it did.
            layout: popup?.layout,
            position: popup?.position,
            // Tint the card + form host to the form's theme. themeVars is the
            // precomputed `--kiroku-*` string from the JSON embed; absent on an
            // older cached response, in which case openModal keeps its default
            // card styling.
            vars: formData.themeVars,
            // The owner's own stylesheet for this form (FormThemeTab), scoped
            // to the dialog by openModal/applyCustomCss so a bare selector in
            // it cannot reach the host page. Unapplied until now: the popup
            // rendered the theme's colors but never the form's custom CSS.
            customCSS: formData.settings?.customCSS?.code,
            // The one reassurance line, from the privacy-policy link the form
            // already carries. The embed's own React form draws no notice (the
            // inline LiveForm does), so without this a popup collecting an
            // email said nothing about where the data goes. The shell renders
            // at most one, so a form host that grows its own stays single.
            privacyPolicyUrl: formData.settings?.privacyPolicyUrl,
            // Fires on Escape, a backdrop click, or the X button -- a close
            // with no decline and no submit, since both of those close the
            // modal through `closeModal()` directly rather than through this
            // (see `handleUserClose` in popupModal.ts). Task 4.2: counted as
            // `popup_dismissed`, behind the same `sendPopupBeacon` consent
            // gate as `shown`/`converted`/`declined`/`offer_accepted`.
            onClose: () => {
              if (config.debug) {
                console.log("KirokuEmbed: popup dismissed");
              }
              sendPopupBeacon({
                event: "dismissed",
                trigger,
                msOpen: Date.now() - shownAt,
                url: window.location.href,
                referrer: document.referrer || "",
                timestamp: new Date().toISOString(),
              });
            },
            offer:
              offerScreen?.enabled && !userInitiated
                ? {
                    question: offerScreen.question,
                    yesLabel: offerScreen.yesLabel,
                    noLabel: offerScreen.noLabel,
                    onYes: () => {
                      sendPopupBeacon({
                        event: "offer_accepted",
                        trigger,
                        url: window.location.href,
                        referrer: document.referrer || "",
                        timestamp: new Date().toISOString(),
                      });
                    },
                    onNo: handleDecline,
                  }
                : undefined,
          });
        };
        if (openNow) {
          showModal(true);
        } else {
          popupTrigger.arm(showModal);
        }
      }

      if (config.debug) {
        console.log("KirokuEmbed: React mode render complete");
      }
    } catch (error) {
      console.error("KirokuEmbed: Failed to load form", error);
      container.innerHTML = `
        <div style="padding: 1rem; border: 1px solid #dc2626; border-radius: 0.375rem; background: #fef2f2; color: #dc2626;">
          <strong>Error loading form</strong>
          <p style="margin: 0.5rem 0 0 0; font-size: 0.875rem;">Unable to load the form. Please try again later.</p>
        </div>
      `;
    }
  }

  /**
   * Best-effort popup analytics beacon (shown / converted / declined). POSTs the
   * given body to the view endpoint with the same guarded keepalive shape used
   * everywhere: a rejected or throwing beacon is swallowed so it never blocks the
   * modal open, submit, or close. The `event` key on the body picks the signal.
   */
  function sendViewBeacon(
    config: EmbedConfig,
    body: Record<string, any>,
  ): void {
    try {
      fetch(`${config.apiUrl}/api/forms/${config.formId}/view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* a failed analytics beacon must not block the modal */
    }
  }

  /**
   * Track a form view event by POSTing to the view endpoint.
   * Fires once per embed initialization (fire-and-forget).
   */
  function trackView(config: EmbedConfig): void {
    const viewEndpoint = `${config.apiUrl}/api/forms/${config.formId}/view`;

    try {
      fetch(viewEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          referrer: document.referrer || "",
          url: window.location.href,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        }),
        keepalive: true,
      }).catch((error) => {
        if (config.debug)
          console.warn("KirokuEmbed: View tracking error:", error);
      });

      if (config.debug) {
        console.log(
          `KirokuEmbed: Tracked view for form ${config.formId} to ${viewEndpoint}`,
        );
      }
    } catch (error) {
      if (config.debug)
        console.warn("KirokuEmbed: Error tracking view:", error);
    }
  }

  /**
   * Main initialization function
   */
  async function initEmbed(
    config: Partial<EmbedConfig>,
    originatingScript?: HTMLScriptElement,
  ): Promise<void> {
    // Merge with defaults
    const fullConfig: EmbedConfig = {
      formId: config.formId || "",
      target: config.target,
      mode: config.mode || "html",
      css: config.css || "external",
      apiUrl: config.apiUrl || DEFAULT_API_URL,
      debug: config.debug || false,
      triggerOverride: config.triggerOverride,
      popupOptOut: config.popupOptOut,
    };

    if (!fullConfig.formId) {
      console.error("KirokuEmbed: formId is required");
      return;
    }

    // Prefer the exact originating script (set by autoInit); fall back to a
    // first-match lookup for manual window.KirokuEmbed.init() callers.
    let scriptElement: HTMLScriptElement | undefined = originatingScript;
    if (!scriptElement) {
      const scripts = document.querySelectorAll("script[data-kiroku-form-id]");
      scriptElement = Array.from(scripts).find(
        (s) => s.getAttribute("data-kiroku-form-id") === fullConfig.formId,
      ) as HTMLScriptElement | undefined;
    }

    // Resolve target container
    const container = resolveTarget(fullConfig.target, scriptElement);

    if (!container) {
      console.error("KirokuEmbed: Could not resolve target container");
      return;
    }

    // Show loading state
    container.innerHTML = `
      <div style="padding: 1rem; text-align: center; color: #64748B;">
        <span>Loading form...</span>
      </div>
    `;

    // Track form view for analytics (fire-and-forget, both modes)
    trackView(fullConfig);

    // Embed based on mode
    if (fullConfig.mode === "react") {
      await embedReactMode(fullConfig, container);
    } else {
      await embedHtmlMode(fullConfig, container);
    }
  }

  /**
   * Auto-initialize every embed script tag on the page exactly once.
   * The data-kiroku-initialized sentinel makes this safe against:
   *   - DOMContentLoaded + late readyState re-entry
   *   - Multiple copies of embed.js on the same page
   *   - Same form id embedded multiple times in separate script tags
   * Must be set synchronously (before awaiting initEmbed) so concurrent
   * IIFE runs don't both pick up the same element.
   */
  function autoInit() {
    const scripts = document.querySelectorAll<HTMLScriptElement>(
      "script[data-kiroku-form-id]:not([data-kiroku-initialized])",
    );
    scripts.forEach((scriptEl) => {
      scriptEl.setAttribute("data-kiroku-initialized", "true");
      const config = parseConfigFromElement(scriptEl);
      if (config) {
        initEmbed(config, scriptEl);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }

  /**
   * Programmatic open: render `formId` as a React-mode popup and open the modal
   * immediately, regardless of the form's settings.popup config. Host-page
   * decided, so it bypasses the frequency cap. Failures (unknown id, config
   * fetch error) are swallowed inside a detached container so the host page is
   * never disturbed.
   */
  async function openForm(
    formId: string,
    opts: OpenOptions = {},
  ): Promise<void> {
    if (!formId) {
      console.error("KirokuForms.open: formId is required");
      return;
    }
    const config: EmbedConfig = {
      formId,
      target: undefined,
      mode: "react",
      css: opts.css || "external",
      apiUrl: opts.apiUrl || DEFAULT_API_URL,
      debug: opts.debug || false,
    };
    // Detached host: embedReactMode renders the popup into its own hidden node
    // and only writes errors here, so nothing lands in the host page's layout.
    const host = document.createElement("div");
    await embedReactMode(config, host, { openNow: true, values: opts.values });
  }

  /** Delegated opener: a click on [data-kiroku-open="FORM_ID"] opens that form. */
  function onDelegatedOpenClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const opener = target.closest("[data-kiroku-open]");
    if (!opener) return;
    const formId = opener.getAttribute("data-kiroku-open");
    if (formId) {
      event.preventDefault();
      // Resolve open() on the global at call time so late script loads still win.
      void window.KirokuForms?.open?.(formId);
    }
  }

  /** Document `kiroku:open` CustomEvent: detail.formId picks the form to open. */
  function onKirokuOpenEvent(event: Event): void {
    const detail = (event as CustomEvent).detail || {};
    if (detail.formId) {
      void window.KirokuForms?.open?.(detail.formId, detail);
    }
  }

  // Expose API for manual initialization
  window.KirokuEmbed = {
    version: VERSION,
    init: initEmbed,
  };

  // Extend the shared window.KirokuForms global (owned by kiroku-form.js in HTML
  // mode) with the programmatic open API. Merge, never replace: kiroku-form.js
  // adds init/initAll when the HTML-mode handler loads and must not be clobbered.
  const kirokuForms = (window.KirokuForms = window.KirokuForms || {
    version: VERSION,
  });
  kirokuForms.open = openForm;

  // Bind the delegated openers once per page, even if embed.js is included more
  // than once. The handlers look up open() on the global at event time.
  if (!window.__kirokuOpenApiBound) {
    window.__kirokuOpenApiBound = true;
    document.addEventListener("click", onDelegatedOpenClick);
    document.addEventListener("kiroku:open", onKirokuOpenEvent);
  }

  // Debug log: fires if any matching script opted in via data-kiroku-debug="true".
  const anyDebug = Array.from(
    document.querySelectorAll("script[data-kiroku-form-id]"),
  ).some((s) => s.getAttribute("data-kiroku-debug") === "true");
  if (anyDebug) {
    console.log(`KirokuEmbed v${VERSION} loaded`);
  }
})();
